/* HR — Attendance (R2)
 *  endpoints used:
 *    GET    /api/attendance/punches?employee_id=&from_date=&to_date=&page=&page_size=
 *    GET    /api/attendance/punches/:id
 *    POST   /api/attendance/punches                (HR/admin)
 *    PATCH  /api/attendance/punches/:id            (HR/admin)
 *    DELETE /api/attendance/punches/:id            (HR/admin)
 *    GET    /api/attendance/schedules?employee_id=
 *    GET    /api/attendance/schedules/me
 *    POST   /api/attendance/schedules              (HR/admin)
 *    PATCH  /api/attendance/schedules/:id          (HR/admin)
 *    DELETE /api/attendance/schedules/:id          (HR/admin)
 *    GET    /api/attendance/daily?employee_id=&from_date=&to_date=&page=&page_size=
 *    GET    /api/attendance/daily/:id
 *    POST   /api/attendance/daily                  (HR/admin)
 *    PATCH  /api/attendance/daily/:id              (HR/admin)
 *    DELETE /api/attendance/daily/:id              (HR/admin)
 *    POST   /api/attendance/daily/recompute        (HR/admin)
 */

const WEEKDAYS = ['', '一', '二', '三', '四', '五', '六', '日'];

/* ───────────── Expressive UI page CSS (plan-expressive-inner-pages.md §2(c) / §4.1 P1) ─────────────
 * No-op under Bento (GxCore.injectCss only inserts when the Expressive shell is on); the router
 * fallback removes every gx-css-* style. Only covers what inline styles cannot: the index.html
 * breakpoint rules that use !important (lede KPI padding/borders/gap) and press-state shape morph. */
if (window.GxCore) window.GxCore.injectCss('gx-css-p-attendance', String.raw`
html[data-ui-shell="expressive"] .gx-main .va2-lede-stats.gx-att-lede{gap:12px!important;border-bottom:0!important}
html[data-ui-shell="expressive"] .gx-main .va2-lede-stats.gx-att-lede > div{padding:16px 20px!important;border:0!important;border-radius:16px;background:var(--gx-card);min-width:0}
html[data-ui-shell="expressive"] .gx-main .hr-kiosk-btn{transition:border-radius .4s var(--gx-spring),transform .4s var(--gx-spring),background .2s}
html[data-ui-shell="expressive"] .gx-main .hr-kiosk-btn:not(:disabled):hover{transform:none}
html[data-ui-shell="expressive"] .gx-main .hr-kiosk-btn:not(:disabled):active{border-radius:10px!important;transform:scale(.98)}
@media (max-width: 767px){
html[data-ui-shell="expressive"] .gx-main .hr-attendance-summary-grid{gap:8px!important}
html[data-ui-shell="expressive"] .gx-main .gx-att-cal-card > div:first-child{padding:12px 16px!important}
html[data-ui-shell="expressive"] .gx-main .gx-att-cal-body{padding:12px 12px 16px!important}
}
@media (prefers-reduced-motion: reduce){
html[data-ui-shell="expressive"] .gx-main .hr-kiosk-btn:not(:disabled):active{transform:none}
}
`);

/* ───────────────────────── Time helpers (TPE-aware) ─────────────────────────
 * 系統一律以「台北時間 (UTC+8)」呈現給管理員操作。後端存 UTC ISO。
 * 契約：
 *   - 顯示：UTC ISO  →  isoToTPELocal(s)  →  "YYYY-MM-DD HH:MM"
 *   - 送出：本地字串 →  tpeLocalToISO(s)  →  "YYYY-MM-DDTHH:MM:00+08:00"
 */
function isoToTPELocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const tpe = new Date(d.getTime() + 8 * 3600 * 1000);
  return tpe.toISOString().slice(0, 16).replace('T', ' ');
}
function tpeLocalToISO(local) {
  if (!local) return null;
  const s = String(local).replace(' ', 'T').slice(0, 16);
  if (s.length < 16) return null;
  return `${s}:00+08:00`;
}
function isoToTPEDate(iso) {
  // ISO → "YYYY-MM-DD" (TPE)
  if (!iso) return '';
  const local = isoToTPELocal(iso);
  return local.slice(0, 10);
}
function isoToTPEHHMM(iso) {
  if (!iso) return '';
  return isoToTPELocal(iso).slice(11, 16);
}

/* 計算打卡顯示工時：若時段與午休 12:00–12:30 重疊，扣除 30 分鐘 */
function calcNetWorkMinutes(p) {
  if (p.work_minutes == null) return null;
  if (!p.punch_time || !p.abort_time) return p.work_minutes;
  const startStr = isoToTPELocal(p.punch_time);
  const endStr   = isoToTPELocal(p.abort_time);
  if (!startStr || !endStr || startStr.length < 16 || endStr.length < 16) return p.work_minutes;
  const startMin = parseInt(startStr.slice(11, 13)) * 60 + parseInt(startStr.slice(14, 16));
  const endMin   = parseInt(endStr.slice(11, 13))  * 60 + parseInt(endStr.slice(14, 16));
  const overlapStart = Math.max(startMin, 720); // 12:00
  const overlapEnd   = Math.min(endMin,   750); // 12:30
  if (overlapEnd > overlapStart) return Math.max(0, p.work_minutes - 30);
  return p.work_minutes;
}

/* ─────────────────────────  Kiosk (Odoo-style 打卡鐘)  ──────── */
/* Single-purpose punch interface modelled on Odoo HR Attendance kiosk:
 *
 *   ┌──────────────────────────────────────────┐
 *   │  2026-05-07 · 星期三                      │
 *   │  14:23:18                  (live, ticking)│
 *   │                                            │
 *   │   [Avatar]   Hi, Mason                    │
 *   │              Genesis · Trader              │
 *   │                                            │
 *   │      ╔═══════════════════════╗            │
 *   │      ║    ⏰  上班打卡         ║   (big CTA)│
 *   │      ╚═══════════════════════╝            │
 *   │                                            │
 *   │   今日狀態 · 排班 · 最近打卡               │
 *   └──────────────────────────────────────────┘
 *
 * State machine (driven by today's punch_records — primary source of truth):
 *   off       → no open punch today, no completed today    → button: 上班打卡
 *   working   → open punch exists (abort_time IS NULL)      → button: 下班打卡 + live duration
 *   done      → today already has completed punch           → button disabled "今日已打卡"
 *
 * Schema: punch_records.abort_time NULLABLE — open punch lives in DB, not localStorage.
 *   - 上班打卡: POST /attendance/punches { employee_id, punch_time }
 *   - 下班打卡: POST /attendance/punches/{id}/close  → server fills abort_time
 */

function useNowTicker(intervalMs = 1000) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function fmtClock(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function fmtElapsed(ms) {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(s)}`;
}

/* Mock colleague fixtures — used as fallback when API returns no data
/* 之前用 GET /employees 拉同事清單但那支限 hr/admin → 非 admin 收到 403 → fallback
 * 到 fixture（這份）。現在改打 /api/attendance/roster：任何登入者皆可看、只回顯示用
 * 的非敏感欄位（id/name/position/dept/avatar）+ 後端算好的 has_punch_today。
 * fixture 已不再使用，保留 0 行的 const 也沒意義，整段移除。 */

function KioskTab({ user }) {
  const toast = useToast();
  const now = useNowTicker(1000);

  const [myEmp, setMyEmp] = React.useState(null);
  const [todayPunches, setTodayPunches] = React.useState([]);
  const [schedules, setSchedules] = React.useState([]);
  const [colleagues, setColleagues] = React.useState([]);          // [{ id, name, position, department_name, hasPunchToday }]
  const [busy, setBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [flash, setFlash] = React.useState(null);                  // 'in' | 'out' | null — for success flash on button
  const [confirmOut, setConfirmOut] = React.useState(false);       // clock-out confirm dialog (SPEC §5.1)

  const empId = myEmp?.id;
  const isHR = isAttendanceManager(user);
  const hasRealEmp = !!empId;

  // 進行中的打卡：從 today punches 找 abort_time IS NULL 的那筆
  const openPunch = todayPunches.find(p => !p.abort_time);
  const punchInAt = openPunch?.punch_time || null;

  /* === Load my employee + my schedules === */
  React.useEffect(() => {
    Promise.all([
      api('GET', '/employees/me').catch(() => null),
      api('GET', `/attendance/schedules/me`).catch(() => []),
    ]).then(([me, sch]) => {
      setMyEmp(me);
      setSchedules(Array.isArray(sch) ? sch : []);
      setLoaded(true);
    });
  }, []);

  /* Refresh today's punches + colleagues (always, with fallback to fixtures) */
  React.useEffect(() => {
    if (!loaded) return;
    refreshTodayAndColleagues();
    const handlePunchUpdate = () => {
      refreshTodayAndColleagues();
    };
    window.addEventListener('roster:updated', handlePunchUpdate);
    const t = setInterval(refreshTodayAndColleagues, 30_000);
    return () => {
      window.removeEventListener('roster:updated', handlePunchUpdate);
      clearInterval(t);
    };
  }, [loaded, empId]);

  async function refreshTodayAndColleagues() {
    // 在呼叫當下重算台北當日 —— 此函式會被 30 秒 interval / roster:updated 的
    // 舊 closure 呼叫（effect 只依 [loaded, empId]），若沿用 render 時捕獲的
    // 外層 today，看板過午夜後會一直查昨天。
    const today = todayStr();
    // Self today's punches (only if we have a real employee record)
    if (empId) {
      const myP = await api('GET', `/attendance/punches?employee_id=${empId}&from_date=${today}&to_date=${today}&page_size=20`).catch(() => ({ items: [] }));
      setTodayPunches(myP.items || []);
    }

    // 公司通訊錄 + 今日打卡狀態（任何登入者皆可看；後端已 server-side 算 has_punch_today）
    const roster = await api('GET', '/attendance/roster').catch(() => []);
    const list = Array.isArray(roster) ? roster : [];
    const others = list.filter(e => e.id !== empId);
    setColleagues(others.map(e => ({
      id: e.id,
      name: e.name,
      position: e.position,
      department_name: e.department_name,
      avatar_url: e.avatar_url,
      hasPunchToday: !!e.has_punch_today,
    })));
  }

  /* === State machine — driven by DB punch records === */
  const completedToday = todayPunches.filter(p => !!p.abort_time);
  const todayDone = completedToday.length > 0;
  const state = openPunch ? 'working'
    : todayDone ? 'done'
      : 'off';
  const isCheckedIn = state === 'working';
  // Punch closed elsewhere (other tab / HR) while the confirm is up → drop it, so it can't reappear later.
  React.useEffect(() => { if (!isCheckedIn) setConfirmOut(false); }, [isCheckedIn]);

  /* Today's schedule for current weekday (1=Mon..7=Sun; JS getDay: 0=Sun) */
  const jsDay = now.getDay();
  const isoWeekday = jsDay === 0 ? 7 : jsDay;
  const todaySchedule = schedules.find(s => s.weekday === isoWeekday);

  const totalWorkedMs = completedToday.reduce((s, p) => s + ((p.work_minutes || 0) * 60_000), 0)
    + (punchInAt ? Math.max(0, now - new Date(punchInAt)) : 0);

  /* Colleague split */
  const onDuty = colleagues.filter(c => c.hasPunchToday);
  const offDuty = colleagues.filter(c => !c.hasPunchToday);

  /* === Actions === */
  const punchIn = async () => {
    if (busy || isCheckedIn) return;
    if (!hasRealEmp) {
      toast('您的帳號尚未綁定員工 — 請聯絡 HR', 'error');
      return;
    }
    setBusy(true);
    try {
      // 即時打卡：不送 punch_time，由後端 utc_now_iso 產生（避免瀏覽器時鐘 / 時區干擾）。
      await api('POST', '/attendance/punches', {
        employee_id: empId,
        remark: null,
      });
      setFlash('in');
      toast('已上班打卡，下班前請記得再打一次', 'ok');
      await refreshTodayAndColleagues();
      window.dispatchEvent(new CustomEvent('roster:updated'));
      setTimeout(() => setFlash(null), 1500);
    } catch (e) {
      toast(e.detail || '打卡失敗', 'error');
    } finally { setBusy(false); }
  };

  // 下班打卡 asks first (owner decision 2026-10-19): the button opens PunchOutConfirm, whose
  // confirm runs punchOut(). 上班打卡 stays one tap.
  const askPunchOut = () => { if (openPunch && !busy) setConfirmOut(true); };

  const punchOut = async () => {
    if (!openPunch) { setConfirmOut(false); return; }
    if (busy) return;
    setBusy(true);
    try {
      await api('POST', `/attendance/punches/${openPunch.id}/close`);
      setFlash('out');
      toast('打卡成功，辛苦了！', 'ok');
      await refreshTodayAndColleagues();
      window.dispatchEvent(new CustomEvent('roster:updated'));
      setTimeout(() => setFlash(null), 1800);
    } catch (e) {
      toast(e.detail || '下班打卡失敗', 'error');
    } finally { setBusy(false); setConfirmOut(false); }
  };

  const cancelInProgress = async () => {
    if (!openPunch) return;
    if (!confirm('取消當前的上班打卡？此筆記錄會被刪除（僅 HR 可刪，一般員工請聯絡 HR）')) return;
    try {
      await api('DELETE', `/attendance/punches/${openPunch.id}`);
      toast('已取消', 'info');
      await refreshTodayAndColleagues();
      window.dispatchEvent(new CustomEvent('roster:updated'));
    } catch (e) {
      toast(e.detail || '取消失敗（DELETE 限 HR/admin）', 'error');
    }
  };

  if (!loaded) {
    return <div style={{ padding: 80, display: 'grid', placeItems: 'center' }}><Spinner size={20} /></div>;
  }

  /* === Date / weekday display === */
  // Same format as the Expressive home greeting / ticker date (「10月16日 星期五」), Taipei calendar day (SPEC §6).
  const dateLine = now.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'long', timeZone: 'Asia/Taipei' });

  /* In-progress live work duration */
  const liveDuration = punchInAt ? fmtElapsed(now - new Date(punchInAt)) : null;
  const inTimeStr = punchInAt ? fmtTime(punchInAt) : null;
  const lateMin = (punchInAt && todaySchedule)
    ? Math.max(0, (new Date(punchInAt).getHours() * 60 + new Date(punchInAt).getMinutes()) - parseTimeMinutes(todaySchedule.start_time))
    : 0;

  const gc = GxT.color;

  return (
    <>
      {/* Animation keyframes — scoped via class names so we don't pollute global */}
      <style>{`
        @keyframes hr-kiosk-spin     { to { transform: rotate(360deg); } }
        @keyframes hr-kiosk-blink    { 0%,100% { opacity: 1 } 50% { opacity: .25 } }
        @keyframes hr-kiosk-pulse    { 0% { transform: scale(1); opacity: .55 } 100% { transform: scale(1.18); opacity: 0 } }
        @keyframes hr-kiosk-fadein   { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }

        .hr-kiosk-ring::before {
          content: ''; position: absolute; inset: -4px; border-radius: 50%;
          animation: hr-kiosk-spin 5s linear infinite;
        }
        .hr-kiosk-ring.is-in::before {
          background: conic-gradient(from 0deg, transparent 0%, ${HR.accent} 38%, transparent 62%);
        }
        .hr-kiosk-ring.is-out::before {
          background: conic-gradient(from 0deg, transparent 0%, ${HR.down} 38%, transparent 62%);
        }
        .hr-kiosk-dot.is-in { animation: hr-kiosk-blink 2.2s ease-in-out infinite; }

        .hr-kiosk-btn.active::after {
          content: ''; position: absolute; inset: -2px; border-radius: inherit;
          border: 2px solid currentColor; opacity: .55;
          animation: hr-kiosk-pulse 2.4s ease-out infinite;
          pointer-events: none;
        }
        .hr-kiosk-btn:not(:disabled):not(.inactive):hover {
          transform: translateY(-1px);
        }
        .hr-kiosk-btn:not(:disabled):not(.inactive):active {
          transform: translateY(0) scale(.98);
        }

        .hr-kiosk-card-fade { animation: hr-kiosk-fadein 240ms ease both; }

        .hr-kiosk-workspace {
          display: grid;
          grid-template-columns: 440px 1fr 1fr;
          gap: 18px;
          transition: grid-template-columns 420ms cubic-bezier(.4,.0,.2,1);
          /* No align-items: default 'stretch' makes all 3 columns share row height */
        }
        .hr-kiosk-workspace[data-mode="mono"] {
          grid-template-columns: 440px 0fr 0fr;
        }
        .hr-kiosk-workspace[data-mode="mono"] .hr-kiosk-side {
          opacity: 0; pointer-events: none;
        }
        .hr-kiosk-side {
          opacity: 1; transition: opacity 280ms ease 80ms;
          overflow: hidden;
        }
      `}</style>

      {/* （舊版的 HR-only banner 已移除 — 後端現允許員工自助上/下班打卡） */}

      {/* 3-column workspace — Card pinned top-left; side panels fill remaining space.
          When off-site, side panels collapse to 0fr (Card stays). */}
      <div className="hr-kiosk-workspace" data-mode={isCheckedIn ? 'tri' : 'mono'}>

        {/* LEFT — personal punch card + today's punches (both pinned in left column) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: uiPick(18, 16) }}>
          <div className="hr-kiosk-card-fade" style={uiStyle({
            background: HR.surface, border: `1px solid ${HR.rule}`,
            borderLeft: isCheckedIn ? `3px solid ${HR.accent}` : `1px solid ${HR.rule}`,
            borderRadius: 10, padding: '36px 32px 24px',
            boxShadow: '0 1px 0 rgba(14,20,17,0.04), 0 12px 36px rgba(14,20,17,0.06)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'flex-start', gap: 18,
            position: 'relative',
            transition: 'border-left-color 240ms ease, border-left-width 240ms ease',
          }, {
            /* borderless 16px card on the warm page background (mockup .card) */
            background: gc.card, border: 'none', borderLeft: 'none', borderRadius: GxT.radius.card,
            boxShadow: 'none', padding: '28px 24px 24px', gap: 16, transition: 'none',
          })}>
            {/* Floating clock + date — top-right of Card (replaces standalone top bar) */}
            <div style={uiStyle({ position: 'absolute', top: 14, right: 18, textAlign: 'right' }, { top: 16, right: 20 })}>
              <div style={uiStyle({
                fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 600,
                color: HR.ink, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em',
              }, { fontFamily: GxT.font, fontSize: 17, fontWeight: 800, letterSpacing: 0, color: gc.on })}>{fmtClock(now)}</div>
              <div style={uiStyle({
                fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 2,
                letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: HR.mono,
              }, { fontSize: 12, fontWeight: 700, color: gc.p, letterSpacing: 0, textTransform: 'none', fontFamily: GxT.font })}>{dateLine}</div>
            </div>

            {/* Avatar with rotating ring */}
            <div className={`hr-kiosk-ring ${isCheckedIn ? 'is-in' : 'is-out'}`} style={{
              position: 'relative', width: 96, height: 96, marginTop: uiPick(14, 28),
            }}>
              <div style={uiStyle({
                position: 'absolute', inset: 4, borderRadius: '50%',
                background: '#D6CEB9', color: HR.ink,
                display: 'grid', placeItems: 'center',
                fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 600,
                border: `2px solid ${HR.surface}`,
                zIndex: 1,
              }, { background: gc.tc, color: gc.onTc, fontSize: 30, fontWeight: 900, border: `3px solid ${gc.card}` })}>
                {(myEmp?.name || user?.username || '?').slice(0, 1)}
              </div>
            </div>

            {/* Name + dept */}
            <div style={{ textAlign: 'center' }}>
              <div style={uiStyle({
                fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 500,
                letterSpacing: '-0.01em', color: HR.ink,
              }, { fontSize: GxT.size.h2, fontWeight: 900, letterSpacing: '-0.3px', lineHeight: 1.15 })}>
                Hi, {myEmp?.name || user?.username || '—'}
              </div>
              <div style={uiStyle({
                fontSize: FontSizes.xxs, color: HR.inkFaint,
                letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 4,
              }, { fontSize: 13, fontWeight: 600, color: gc.onV, letterSpacing: 0, textTransform: 'none' })}>
                {myEmp?.department_name || '未指派部門'}{myEmp?.position && ` · ${myEmp.position}`}
              </div>
            </div>

            {/* Status — natural sentence (replaces badge + standalone timer) */}
            <div style={{
              width: '100%', padding: '14px 0', borderTop: `1px solid ${HR.ruleSoft}`,
              textAlign: 'center',
            }}>
              {state === 'working' && (
                <>
                  <div style={{
                    fontFamily: HR.serif, fontSize: uiPick(FontSizes.xs, 15), color: HR.ink, lineHeight: 1.4,
                  }}>
                    你已上班 <strong style={uiStyle({
                      fontFamily: HR.mono, fontWeight: 600, color: HR.accent,
                      fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em',
                    }, { fontFamily: GxT.font, fontWeight: 800, letterSpacing: 0, fontSize: 17 })}>{liveDuration}</strong>
                  </div>
                  <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft, marginTop: 6 }}>
                    上班於 <span style={{ fontFamily: HR.mono }}>{inTimeStr}</span>
                    {lateMin > 0 && <span style={{ color: uiPick(HR.warn, GxT.color.t), marginLeft: 6, fontWeight: uiPick(undefined, 700) }}>· 較預定晚 {fmtDuration(lateMin)}</span>}
                  </div>
                </>
              )}
              {state === 'off' && (
                <>
                  <div style={{
                    fontFamily: HR.serif, fontSize: uiPick(FontSizes.xs, 15), color: HR.inkSoft, fontStyle: uiPick('italic', 'normal'),
                    fontWeight: uiPick(undefined, 600),
                  }}>
                    今日尚未打卡
                  </div>
                  <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 6, fontFamily: HR.mono }}>
                    {todaySchedule
                      ? `排班 ${todaySchedule.start_time} – ${todaySchedule.end_time}`
                      : '今日無排班 — 仍可自由打卡'}
                  </div>
                </>
              )}
              {state === 'done' && (
                <>
                  <div style={{
                    fontFamily: HR.serif, fontSize: uiPick(FontSizes.xs, 15), color: HR.ink, lineHeight: 1.4,
                  }}>
                    今日已完成 · <strong style={uiStyle({
                      fontFamily: HR.mono, fontWeight: 600, color: HR.accent,
                      fontVariantNumeric: 'tabular-nums',
                    }, { fontFamily: GxT.font, fontWeight: 800, fontSize: 17 })}>{fmtDuration(Math.floor(totalWorkedMs / 60000))}</strong>
                  </div>
                  <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 6 }}>
                    辛苦了 — 下方有今日的紀錄
                  </div>
                </>
              )}
            </div>

            {/* SINGLE primary CTA — only the relevant action gets rendered */}
            <div style={{ width: '100%' }}>
              {state === 'off' && (
                <PrimaryPunchBtn kind="in" label="上班打卡" sublabel="CHECK IN"
                  onClick={punchIn} disabled={busy} flashing={flash === 'in'} />
              )}
              {state === 'working' && (
                <PrimaryPunchBtn kind="out" label="下班打卡" sublabel="CHECK OUT"
                  onClick={askPunchOut} disabled={busy} flashing={flash === 'out'} />
              )}
              {state === 'done' && (
                <button onClick={punchIn} disabled={busy} style={uiStyle({
                  width: '100%', padding: '12px 14px', fontSize: FontSizes.xxs,
                  background: 'transparent', border: `1px dashed ${HR.rule}`,
                  borderRadius: 6, color: HR.inkSoft, cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: HR.sans, fontWeight: 500, letterSpacing: '0.06em',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }, {
                  /* tonal secondary action (mockup .btn.tonal) */
                  minHeight: 48, padding: '0 16px', fontSize: 14, fontWeight: 700, letterSpacing: 0,
                  background: busy ? gc.s3 : gc.sc, color: busy ? gc.onV : gc.onSc,
                  border: 'none', borderRadius: GxT.radius.btn, gap: 8,
                })}>
                  <Icon name="refresh" size={uiPick(12, 16)} stroke={2} /> 再次打卡 · 晚班 / 加班
                </button>
              )}
            </div>


            {/* DEMO mode hint (when no real emp record) */}
            {!hasRealEmp && (
              <div style={uiStyle({
                textAlign: 'center', fontSize: FontSizes.xxs, color: HR.inkFaint,
                padding: '6px 10px', background: HR.surfaceAlt, borderRadius: 4,
                fontFamily: HR.mono, letterSpacing: '0.04em', width: '100%',
              }, {
                fontSize: 12, fontWeight: 600, color: GxCore.tone('warn').fg, background: GxCore.tone('warn').bg,
                padding: '8px 12px', borderRadius: GxT.radius.btnSm, fontFamily: GxT.font, letterSpacing: 0,
              })}>
                DEMO 模式 · 你的帳號尚未綁員工，紀錄不會送到後端
              </div>
            )}
          </div>


          {/* Today's punches mini-list (under Card, only when there's something) */}
          {todayPunches.length > 0 && (
            <div style={uiStyle({
              background: HR.surface, border: `1px solid ${HR.rule}`,
              borderRadius: 8, padding: '16px 20px',
            }, { border: 'none', borderRadius: GxT.radius.card, padding: '18px 20px 14px' })}>
              <div style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10,
              }}>
                <div>
                  <div style={uiStyle({ fontSize: FontSizes.xxs, letterSpacing: '0.14em', textTransform: 'uppercase', color: HR.inkFaint }, { fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: gc.p })}>Today</div>
                  <h3 style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.xs, fontWeight: 500, margin: '2px 0 0' }, { fontSize: GxT.size.h3, fontWeight: 800 })}>本日打卡紀錄</h3>
                </div>
                <span style={{ fontSize: FontSizes.xxs, fontFamily: HR.mono, color: HR.inkSoft }}>
                  {todayPunches.length} 段 · {fmtDuration(Math.floor(totalWorkedMs / 60000))}
                </span>
              </div>
              {todayPunches.map((p, i) => (
                <div key={p.id} style={{
                  display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
                  padding: '8px 0', borderTop: i > 0 ? `1px solid ${HR.ruleSoft}` : 'none',
                  fontSize: FontSizes.xxs,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon name="clock" size={12} stroke={2} />
                    <span style={{ fontFamily: HR.mono }}>
                      {fmtTime(p.punch_time)} <span style={{ color: HR.inkFaint }}>→</span> {fmtTime(p.abort_time)}
                    </span>
                  </div>
                  <span style={{ fontFamily: HR.mono, color: HR.inkSoft, fontSize: FontSizes.xxs }}>{fmtDuration(p.work_minutes)}</span>
                  {p.remark && (
                    <span style={{ gridColumn: '1 / -1', fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal'), paddingLeft: 20 }}>
                      {p.remark}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MIDDLE — 已到班 */}
        <div className="hr-kiosk-side">
          <SideRoster
            kind="in"
            title="已到班"
            subtitle="Today on duty"
            employees={onDuty}
            emptyHint="目前無同事打卡"
          />
        </div>

        {/* RIGHT — 未到班 */}
        <div className="hr-kiosk-side">
          <SideRoster
            kind="out"
            title="未到班"
            subtitle="Today off duty"
            employees={offDuty}
            emptyHint="所有人都已打卡 ☕"
          />
        </div>
      </div>

      <PunchOutConfirm open={confirmOut && isCheckedIn} busy={busy}
        onCancel={() => setConfirmOut(false)} onConfirm={punchOut} />
    </>
  );
}

/* === Side roster panel (left = 已到班 / right = 未到班) === */
function SideRoster({ kind, title, subtitle, employees, emptyHint }) {
  const isIn = kind === 'in';
  const accent = isIn ? HR.accent : HR.down;
  const gTone = GxCore.tone(isIn ? 'ok' : 'down');
  const tintBg = isIn ? HR.accentSoft : uiPick('#F4DAD8', gTone.bg);
  const tintBorder = isIn ? GxCore.alpha(HR.accent, '33') : GxCore.alpha(HR.down, '33');
  const gc = GxT.color;

  return (
    <div style={uiStyle({
      background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
      padding: 12, height: '100%', maxHeight: 540,
      display: 'flex', flexDirection: 'column',
    }, { border: 'none', borderRadius: GxT.radius.card, padding: '14px 12px 12px' })}>
      <div style={uiStyle({
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 8px 12px', borderBottom: `1px solid ${HR.ruleSoft}`,
      }, { padding: '4px 8px 10px', borderBottom: 'none' })}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: accent,
          boxShadow: isIn ? `0 0 6px ${GxCore.alpha(accent, '88')}` : 'none',
        }} />
        <div style={{ flex: 1 }}>
          <div style={uiStyle({
            fontSize: FontSizes.xxs, letterSpacing: '0.14em', textTransform: 'uppercase',
            fontWeight: 600, color: accent,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }, { fontSize: GxT.size.h3, letterSpacing: 0, textTransform: 'none', fontWeight: 800, color: gc.on })}>
            {title}
          </div>
          <div style={uiStyle({ fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono, marginTop: 1 }, { fontFamily: GxT.font, fontWeight: 600 })}>
            {subtitle}
          </div>
        </div>
        <span style={uiStyle({
          fontSize: FontSizes.xxs, padding: '2px 9px', borderRadius: 12,
          background: tintBg, color: accent, border: `1px solid ${tintBorder}`,
          fontFamily: HR.mono, fontWeight: 600,
        }, {
          fontSize: 13, padding: '3px 10px', borderRadius: GxT.radius.chip, border: 'none',
          background: gTone.bg, color: gTone.fg, fontFamily: GxT.font, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        })}>{employees.length}</span>
      </div>

      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '8px 4px 4px',
        display: 'flex', flexDirection: 'column', gap: uiPick(1, 2),
      }}>
        {employees.length === 0 ? (
          <div style={uiStyle({
            padding: '32px 12px', textAlign: 'center',
            fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: 'italic',
          }, { fontStyle: 'normal', fontSize: 13, background: gc.s1, borderRadius: GxT.radius.inner })}>
            {emptyHint}
          </div>
        ) : employees.map(e => (
          <a
            key={e.id}
            href={`#/employees?q=${encodeURIComponent(e.name)}`}
            title={`查看 ${e.name} 的員工資料`}
            style={uiStyle({
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 8px', borderRadius: 4,
              background: 'transparent', textDecoration: 'none', color: 'inherit',
              transition: 'background 120ms',
            }, { minHeight: 40, padding: '6px 8px', gap: 10, borderRadius: GxT.radius.btnSm })}
            onMouseEnter={(ev) => ev.currentTarget.style.background = tintBg}
            onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
          >
            <div style={uiStyle({
              width: 22, height: 22, borderRadius: '50%',
              background: '#D6CEB9', color: HR.ink,
              display: 'grid', placeItems: 'center',
              fontSize: FontSizes.xxs, fontWeight: 600, fontFamily: HR.serif,
              border: `1.5px solid ${GxCore.alpha(accent, '55')}`, flexShrink: 0,
            }, { width: 28, height: 28, background: gc.tc, color: gc.onTc, fontWeight: 800, border: `2px solid ${GxCore.alpha(accent, '55')}` })}>{(e.name || '?').slice(0, 1)}</div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{
                fontSize: uiPick(FontSizes.xxs, 14), color: HR.ink, fontWeight: uiPick(500, 700),
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                flexShrink: 0,
              }}>{e.name}</span>
              {(e.position || e.department_name) && (
                <span style={{
                  fontSize: FontSizes.xxs, color: HR.inkFaint,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  flex: 1, textAlign: 'right',
                }}>
                  {e.position || e.department_name}
                </span>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

/* === Primary punch button (full-width, single CTA per state) ===
 * Replaces the old paired CHECK IN/OUT row. Renders only the button that
 * matches the user's current state — no inactive sibling to clutter the UI.
 */
function PrimaryPunchBtn({ kind, label, sublabel, onClick, disabled, flashing }) {
  const isIn = kind === 'in';
  const fg = isIn ? HR.accent : HR.down;
  const bg = isIn ? HR.accentSoft : '#F4DAD8';
  // Expressive: filled primary (上班) / warm (下班, same meaning as the shell FAB), mockup .btn.lg
  const gc = GxT.color;
  const gFill = isIn ? gc.p : gc.warm;
  const gTone = GxCore.tone(isIn ? 'ok' : 'down');

  if (flashing) {
    return (
      <div role={uiPick(undefined, 'status')} style={uiStyle({
        width: '100%', padding: '20px 16px', borderRadius: 8,
        background: bg, border: `1px solid ${fg}`, color: fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        fontFamily: HR.sans, fontWeight: 700, fontSize: FontSizes.xs, letterSpacing: '0.06em',
      }, {
        minHeight: 64, padding: '0 16px', borderRadius: GxT.radius.btnLg, border: 'none',
        background: gTone.bg, color: gTone.fg, fontWeight: 800, fontSize: 16, letterSpacing: 0,
      })}>
        ✓ {isIn ? '已記錄上班 · PUNCHED IN' : '已記錄下班 · PUNCHED OUT'}
      </div>
    );
  }

  return (
    <button
      className="hr-kiosk-btn active"
      onClick={disabled ? null : onClick}
      disabled={disabled}
      style={uiStyle({
        position: 'relative',
        width: '100%', padding: '18px 16px', borderRadius: 8,
        background: fg, color: '#fff',
        border: `1px solid ${fg}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
        fontFamily: HR.sans, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        boxShadow: `0 4px 12px ${GxCore.alpha(fg, '33')}, inset 0 -2px 0 rgba(0,0,0,0.12)`,
        transition: 'transform 120ms, box-shadow 120ms',
        letterSpacing: '0.04em',
      }, {
        minHeight: 64, padding: '10px 16px', borderRadius: GxT.radius.btnLg, border: 'none',
        background: disabled ? gc.s3 : gFill, color: disabled ? gc.onV : gc.onP,
        opacity: 1, boxShadow: 'none', fontWeight: 800, letterSpacing: 0, gap: 3,
        transition: 'border-radius .4s var(--gx-spring), transform .4s var(--gx-spring), background .2s',
      })}
    >
      <span style={uiStyle({ fontSize: FontSizes.xs, lineHeight: 1 }, { fontSize: 18, lineHeight: 1.15 })}>{label}</span>
      <span style={uiStyle({
        fontSize: FontSizes.xxs, opacity: 0.85, letterSpacing: '0.18em',
        fontFamily: HR.mono, fontWeight: 600,
      }, { fontSize: 11, opacity: 0.9, letterSpacing: '0.08em', fontFamily: GxT.font, fontWeight: 700 })}>{sublabel}</span>
    </button>
  );
}

/* === Single punch button (kept for backwards-compat; not used after redesign) === */
function PunchBtn({ kind, active, inactive, disabled, flashing, onClick, label, icon }) {
  const isIn = kind === 'in';
  const color = isIn ? HR.accent : HR.down;
  const tintBg = isIn ? HR.accentSoft : uiPick('#F4DAD8', GxCore.tone('down').bg);

  // Flashing success state
  if (flashing) {
    return (
      <button
        disabled
        style={{
          padding: '14px 8px', borderRadius: uiPick(6, GxT.radius.btn),
          background: tintBg, border: `1px solid ${color}`, color,
          fontSize: FontSizes.xxs, fontWeight: 700, letterSpacing: '0.1em',
          fontFamily: HR.sans, cursor: 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        ✓ {isIn ? 'PUNCHED IN' : 'PUNCHED OUT'}
      </button>
    );
  }

  const baseStyle = {
    position: 'relative',
    padding: '14px 8px', borderRadius: uiPick(6, GxT.radius.btn),
    fontSize: FontSizes.xxs, fontWeight: uiPick(700, 800), letterSpacing: uiPick('0.1em', '0.04em'),
    fontFamily: HR.sans,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'transform 120ms, opacity 200ms, background 200ms',
    cursor: disabled ? 'not-allowed' : (inactive ? 'default' : 'pointer'),
  };

  if (inactive) {
    return (
      <button disabled style={{
        ...baseStyle,
        background: HR.surface, border: `1px solid ${HR.ruleSoft}`,
        color: HR.inkFaint, opacity: 0.55,
      }}>
        <Icon name={icon} size={12} stroke={2} />
        {label}
      </button>
    );
  }

  return (
    <button
      className={`hr-kiosk-btn ${active ? 'active' : ''}`}
      onClick={disabled ? null : onClick}
      disabled={disabled}
      style={{
        ...baseStyle,
        background: tintBg, border: `1px solid ${color}`, color,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Icon name={icon} size={12} stroke={2} />
      {label}
    </button>
  );
}

/* Expressive style patches shared by PunchForm / AdminLeaveRequestForm (render-time only;
 * passed as the 2nd arg of uiStyle, so Bento never sees them). */
function gxLockedHintPatch() {
  return { background: GxT.color.pc, color: GxT.color.onPc, border: 'none', borderRadius: GxT.radius.inner, fontWeight: 600 };
}
function gxPresetLabelPatch() {
  return { fontSize: 13, fontWeight: 700, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none' };
}
function gxSideSheetPatch() {
  // M3 side sheet: rounded leading corners, surface-container background, modal elevation
  return { background: GxT.color.s1, borderRadius: GxT.radius.drawer, boxShadow: GxT.shadow.modal, overflow: 'hidden' };
}
function gxSheetSectionLabelPatch() {
  return { fontSize: 13, fontWeight: 700, color: GxT.color.onV, textTransform: 'none', letterSpacing: 0 };
}
function gxPresetChipPatch(on) {
  // Off-state border/colour stay HR.rule / HR.inkSoft so the existing hover handlers restore them correctly.
  return {
    minHeight: 36, padding: '0 14px', borderRadius: GxT.radius.chip, fontSize: 13, fontWeight: 700,
    border: `1px solid ${on ? GxT.color.sc : HR.rule}`,
    background: on ? GxT.color.sc : GxT.color.card,
    color: on ? GxT.color.onSc : HR.inkSoft,
  };
}

function parseTimeMinutes(hhmm) {
  if (!hhmm || hhmm.length < 4) return 0;
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}

/* ─────────────────────────  我的出勤  ───────────────────────── */

function MyAttendanceTab({ user }) {
  const toast = useToast();
  const [punches, setPunches] = React.useState([]);
  const [daily, setDaily] = React.useState([]);
  const [schedules, setSchedules] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const from30 = nDaysAgoStr(30);
  const today = todayStr();

  const fetchMyAttendance = () => {
    api('GET', '/employees/me').catch(() => null).then(me => {
      const empId = me?.id;
      const empQs = empId ? `&employee_id=${empId}` : '';
      return Promise.all([
        api('GET', `/attendance/punches?from_date=${from30}&to_date=${today}&page_size=50${empQs}`).catch(() => ({ items: [] })),
        api('GET', `/attendance/daily?from_date=${from30}&to_date=${today}&page_size=50${empQs}`).catch(() => ({ items: [] })),
        api('GET', '/attendance/schedules/me').catch(() => []),
      ]);
    }).then(([p, d, s]) => {
      setPunches(p.items || []);
      setDaily(d.items || []);
      setSchedules(Array.isArray(s) ? s : []);
    }).catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    setLoading(true);
    fetchMyAttendance();
    const handlePunchUpdate = () => {
      fetchMyAttendance();
    };
    window.addEventListener('roster:updated', handlePunchUpdate);
    return () => {
      window.removeEventListener('roster:updated', handlePunchUpdate);
    };
  }, []);

  const totalMins = daily.reduce((s, d) => s + (d.work_minutes || 0), 0);
  const todayDaily = daily.find(d => d.work_date === today);
  const lastPunch = punches[0];

  if (loading) {
    return <div style={{ padding: 80, display: 'grid', placeItems: 'center' }}><Spinner size={20} /></div>;
  }

  // Expressive KPI tiles: label = 12/700 on-variant, value = Figtree 800 tabular (mockup .kpis).
  const gc = GxT.color;
  const gxKpiLabel = { fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: gc.onV, marginBottom: 8 };
  const gxKpiValue = { fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' };
  const gxTile = { padding: '16px 20px', borderRight: 'none' };

  return (
    <>
      {/* Lede stats */}
      <div className={GxCore.cls('va2-lede-stats', 'gx-att-lede')} style={uiStyle({
        display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
        borderBottom: `1px solid ${HR.rule}`, paddingBottom: 24, marginBottom: 24,
      }, { borderBottom: 'none', paddingBottom: 0, marginBottom: 20, gap: 12 })}>
        <div style={uiStyle({ paddingRight: 32, borderRight: `1px solid ${HR.rule}` }, gxTile)}>
          <div style={uiStyle({ fontSize: FontSizes.xxs, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 }, gxKpiLabel)}>
            近 30 日工時 · 30-Day Total
          </div>
          <div style={uiStyle({
            fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: 400,
            letterSpacing: '-0.03em', lineHeight: 0.95,
            color: HR.ink, fontVariantNumeric: 'tabular-nums',
          }, { fontSize: 34, fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1.05 })}>
            {Math.floor(totalMins / 60)}{' '}<span style={{ fontSize: FontSizes.sm, color: HR.inkFaint, fontWeight: uiPick(undefined, 700) }}>h</span>{' '}{totalMins % 60}{' '}<span style={{ fontSize: FontSizes.sm, color: HR.inkFaint, fontWeight: uiPick(undefined, 700) }}>m</span>
          </div>
          <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 10 }}>
            共 {daily.length} 天有打卡紀錄
          </div>
        </div>
        <div style={uiStyle({ padding: '0 24px', borderRight: `1px solid ${HR.rule}` }, gxTile)}>
          <div style={uiStyle({ fontSize: FontSizes.xxs, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 }, gxKpiLabel)}>今日工時</div>
          <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, lineHeight: 1, color: todayDaily ? HR.up : HR.inkFaint }, gxKpiValue)}>
            {todayDaily ? fmtDuration(todayDaily.work_minutes) : '—'}
          </div>
          <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 8 }}>
            {todayDaily ? `${todayDaily.actual_in || ''} – ${todayDaily.actual_out || ''}` : '尚無紀錄'}
          </div>
        </div>
        <div style={uiStyle({ padding: '0 24px', borderRight: `1px solid ${HR.rule}` }, gxTile)}>
          <div style={uiStyle({ fontSize: FontSizes.xxs, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 }, gxKpiLabel)}>最近打卡</div>
          <div style={uiStyle({ fontSize: FontSizes.xs, color: HR.ink, marginTop: 14 }, { fontSize: 15, fontWeight: 700, marginTop: 0, fontVariantNumeric: 'tabular-nums' })}>
            {lastPunch ? fmtDateTime(lastPunch.punch_time) : '—'}
          </div>
          {lastPunch && (
            <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 6, fontFamily: HR.mono }}>
              工作 {fmtDuration(calcNetWorkMinutes(lastPunch))}
            </div>
          )}
        </div>
        <div style={uiStyle({ padding: '0 24px' }, gxTile)}>
          <div style={uiStyle({ fontSize: FontSizes.xxs, letterSpacing: '0.16em', textTransform: 'uppercase', color: HR.inkFaint, marginBottom: 12 }, gxKpiLabel)}>排班</div>
          <div style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, lineHeight: 1 }, gxKpiValue)}>
            {schedules.length}
          </div>
          <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 8 }}>個 weekday 規則</div>
        </div>
      </div>

      <div className="hr-attendance-my-records-grid" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24 }}>
        {/* 近期打卡 */}
        <Card eyebrow="Punch Records" title="近期打卡" italic="Recent" padding="0">
          {punches.length === 0 ? (
            <EmptyState icon="clock" title="近 30 日無打卡紀錄" hint="HR 會匯入打卡機資料；如有疑問請聯絡人資。" />
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
                <thead>
                  <tr style={uiStyle({ background: HR.surfaceAlt, fontSize: FontSizes.xxs, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }, { letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontSize: 12 })}>
                    <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>日期</th>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>上班</th>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>下班</th>
                    <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>工時</th>
                  </tr>
                </thead>
                <tbody>
                  {punches.map(p => (
                    <tr key={p.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      <td style={{ padding: '12px 16px 12px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
                        {fmtDate(p.punch_time)}
                      </td>
                      <td style={{ padding: '12px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                        {fmtTime(p.punch_time)}
                      </td>
                      <td style={{ padding: '12px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                        {fmtTime(p.abort_time)}
                      </td>
                      <td style={{ padding: '12px 24px 12px 16px', textAlign: 'right', fontFamily: HR.mono, fontSize: FontSizes.xxs, fontWeight: 500 }}>
                        {fmtDuration(calcNetWorkMinutes(p))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* 我的排班 */}
        <Card eyebrow="Weekly Schedule" title="排班" italic="My Schedule">
          {schedules.length === 0 ? (
            <div style={{ fontSize: FontSizes.xs, color: HR.inkFaint, padding: 12 }}>尚未設定排班規則</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {schedules.map(s => (
                <div
                  key={s.id}
                  style={uiStyle({
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 12px', borderRadius: 4, border: `1px solid ${HR.ruleSoft}`,
                  }, { padding: '10px 12px', borderRadius: GxT.radius.inner, border: 'none', background: GxT.color.s1 })}
                >
                  <div style={uiStyle({
                    width: 28, height: 28, borderRadius: 4,
                    background: s.employee_id ? HR.accentSoft : HR.ruleSoft,
                    color: s.employee_id ? HR.accent : HR.inkSoft,
                    display: 'grid', placeItems: 'center',
                    fontSize: FontSizes.xxs, fontWeight: 600, fontFamily: HR.serif,
                  }, {
                    width: 32, height: 32, borderRadius: GxT.radius.chip, fontSize: 13, fontWeight: 800,
                    background: s.employee_id ? GxT.color.pc : GxT.color.s3,
                    color: s.employee_id ? GxT.color.onPc : GxT.color.onV,
                  })}>
                    {WEEKDAYS[s.weekday]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={uiStyle({ fontSize: FontSizes.xs, fontFamily: HR.mono, color: HR.ink }, { fontSize: 14, fontFamily: GxT.font, fontWeight: 700, fontVariantNumeric: 'tabular-nums' })}>
                      {s.start_time} – {s.end_time}
                    </div>
                    <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>
                      休息 {s.break_minutes} 分 · {s.employee_id ? '個人覆寫' : '全公司預設'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

/* ─────────────────────────  HR — 打卡紀錄  ──────────────────── */

/* PunchForm — 兩種模式：
 *   1) 一般模式（lockedDate 未傳）：日期+時間都可改（給「打卡紀錄」舊 tab 用）
 *   2) 鎖定日期模式（lockedDate='YYYY-MM-DD'）：日期固定為傳入的當日，只能改 HH:MM
 *      跨午夜偵測：若下班 HH:MM <= 上班 HH:MM，視為次日下班（自動 +1 天）
 */
function PunchForm({ initial, onCancel, onSaved, lockedDate = null }) {
  const toast = useToast();
  const isEdit = !!(initial && initial.id);
  const locked = !!lockedDate;

  const [empId, setEmpId] = React.useState(initial?.employee_id || null);
  const [empLabel, setEmpLabel] = React.useState(initial?.employee_name || '');
  // 鎖定模式：只存 HH:MM；一般模式：存 'YYYY-MM-DD HH:MM'
  const [punchTime, setPunchTime] = React.useState(() =>
    locked ? (isoToTPEHHMM(initial?.punch_time) || '') : isoToTPELocal(initial?.punch_time)
  );
  const [abortTime, setAbortTime] = React.useState(() =>
    locked ? (isoToTPEHHMM(initial?.abort_time) || '') : isoToTPELocal(initial?.abort_time)
  );
  const [remark, setRemark] = React.useState(initial?.remark || '');
  const [busy, setBusy] = React.useState(false);

  // 鎖定模式：偵測跨午夜（下班 HH:MM <= 上班 HH:MM）
  const isOvernight = React.useMemo(() => {
    if (!locked || !punchTime || !abortTime) return false;
    const [ph, pm] = punchTime.split(':').map(Number);
    const [ah, am] = abortTime.split(':').map(Number);
    return (ah * 60 + am) <= (ph * 60 + pm);
  }, [locked, punchTime, abortTime]);

  // 鎖定模式：把 lockedDate + HH:MM 組成 ISO；跨午夜時 abort 自動 +1 天
  const buildIsoFromLocked = (timeHHMM, addOneDay = false) => {
    if (!timeHHMM) return null;
    let dateStr = lockedDate;
    if (addOneDay) {
      const d = new Date(lockedDate + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    }
    return `${dateStr}T${timeHHMM}:00+08:00`;
  };

  // 快捷時段（鎖定模式，與代補請假一致）：整天 = 排班起訖；上午半 = 排班起~12:00；下午半 = 13:00~排班迄。
  const _ws = isoToTPEHHMM(initial?.punch_time) || '09:00';
  const _we = isoToTPEHHMM(initial?.abort_time) || '18:00';
  const punchPresets = [
    { key: 'full', label: '整天', from: _ws, to: _we },
    { key: 'am', label: '上午半', from: _ws, to: '12:00' },
    { key: 'pm', label: '下午半', from: '13:00', to: _we },
  ];
  const applyPunchPreset = (p) => { setPunchTime(p.from); setAbortTime(p.to); };
  const activePunchPreset = punchPresets.find(p => p.from === punchTime && p.to === abortTime)?.key;

  const submit = async () => {
    if (!isEdit && !empId) { toast('請選擇員工', 'error'); return; }
    if (!punchTime || !abortTime) { toast('上下班時間必填', 'error'); return; }
    setBusy(true);
    try {
      const payload = {
        punch_time: locked ? buildIsoFromLocked(punchTime) : tpeLocalToISO(punchTime),
        abort_time: locked ? buildIsoFromLocked(abortTime, isOvernight) : tpeLocalToISO(abortTime),
        remark: remark || null,
      };
      if (isEdit) {
        await api('PATCH', `/attendance/punches/${initial.id}`, payload);
        toast('打卡紀錄已更新', 'ok');
      } else {
        await api('POST', '/attendance/punches', { ...payload, employee_id: empId });
        toast('打卡紀錄已新增', 'ok');
      }
      onSaved();
    } catch (e) { toast(e.detail || '儲存失敗', 'error'); } finally { setBusy(false); }
  };

  return (
    <Modal
      open
      onClose={busy ? null : onCancel}
      title={isEdit ? `編輯打卡 — ${initial.employee_name || `#${initial.employee_id}`}` : '新增打卡紀錄'}
      subtitle={isEdit ? 'Edit Punch' : 'New Punch'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '儲存中…' : (isEdit ? '儲存變更' : '新增')}</Button>
        </>
      }
    >
      {locked && (
        <div style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', marginBottom: 14,
          background: '#E6EEED', border: `1px solid #B7CFC9`, borderRadius: 6,
          fontSize: FontSizes.xs, color: '#0B5F5A',
        }, gxLockedHintPatch())}>
          <Icon name="calendar" size={15} />
          <span>日期已鎖定為 <strong style={{ fontFamily: HR.mono }}>{lockedDate}</strong>，只能調整時間</span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {!isEdit && (
          <Field label="員工" required span={2}>
            <EmployeePicker value={empId} valueLabel={empLabel} onChange={(id, label) => { setEmpId(id); setEmpLabel(label || ''); }} />
          </Field>
        )}
        {locked ? (
          <>
            <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={uiStyle({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' }, gxPresetLabelPatch())}>快捷</span>
              {punchPresets.map(p => {
                const on = activePunchPreset === p.key;
                return (
                  <button key={p.key} type="button" onClick={() => applyPunchPreset(p)}
                    aria-pressed={uiPick(undefined, on)}
                    style={uiStyle({
                      padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
                      fontSize: FontSizes.xxs, fontWeight: 600,
                      border: `1px solid ${on ? HR.accent : HR.rule}`,
                      background: on ? HR.accentSoft : HR.surface,
                      color: on ? HR.accent : HR.inkSoft,
                      transition: 'background .12s, border-color .12s, color .12s',
                    }, gxPresetChipPatch(on))}
                    onMouseEnter={(e) => { if (!on) { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.color = HR.accent; } }}
                    onMouseLeave={(e) => { if (!on) { e.currentTarget.style.borderColor = HR.rule; e.currentTarget.style.color = HR.inkSoft; } }}
                  >{p.label}</button>
                );
              })}
            </div>
            <Field label="上班時間 (台北時區)" required>
              <TimeSelect value={punchTime} onChange={setPunchTime} />
            </Field>
            <Field label="下班時間 (台北時區)" required hint={isOvernight ? '⚠ 跨午夜：將存為次日下班' : undefined}>
              <TimeSelect value={abortTime} onChange={setAbortTime} />
            </Field>
          </>
        ) : (
          <>
            <Field label="上班時間 (台北時區)" required>
              <input
                type="datetime-local"
                value={punchTime ? punchTime.replace(' ', 'T') : ''}
                onChange={(e) => setPunchTime(e.target.value.replace('T', ' '))}
                style={inputStyle({ fontFamily: HR.mono })}
              />
            </Field>
            <Field label="下班時間 (台北時區)" required>
              <input
                type="datetime-local"
                value={abortTime ? abortTime.replace(' ', 'T') : ''}
                onChange={(e) => setAbortTime(e.target.value.replace('T', ' '))}
                style={inputStyle({ fontFamily: HR.mono })}
              />
            </Field>
          </>
        )}
        <Field label="備註" span={2}>
          <Textarea value={remark} onChange={setRemark} rows={2} placeholder="加班 / 出差 / 補打卡…" />
        </Field>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  Calendar View (HR / selfMode)  ─────────────────────────
 * HR 流程：部門 → 員工 → 月曆 → 點日期格 → Drawer 編輯/補打卡。
 * selfMode（一般員工）：鎖定看自己（後端 /attendance/calendar 不帶 employee_id 自動 resolve），
 * 唯讀 —— Drawer 內所有 HR-only 操作隱藏。
 * 上下班時間來自 work_schedules（個人覆寫 > 公司預設）。
 * 異常條件（abnormal）：
 *   - 任一段缺 abort_time（忘記下班打卡）
 *   - 當日實工時（sum(work_minutes) - break）< 應工時（end - start - break）
 * 該 weekday 無排班 → 視為休息日（rest），不算異常。
 */

function hhmmToMin(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]), mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  return h * 60 + mm;
}

function computeDayStatus(punches, schedule) {
  if (!schedule) return 'rest';
  if (!punches || !punches.length) return 'none';
  if (punches.some(p => !p.abort_time)) return 'abnormal';
  const required = schedule.endMin - schedule.startMin - (schedule.breakMinutes || 0);
  const raw = punches.reduce((s, p) => s + (p.work_minutes || 0), 0);
  const net = raw - (schedule.breakMinutes || 0);
  return net >= required ? 'normal' : 'abnormal';
}

function ymdStr(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function CalendarLabel({ children }) {
  return (
    <div style={uiStyle({
      fontSize: FontSizes.xxs, letterSpacing: '0.1em', textTransform: 'uppercase',
      color: HR.inkFaint, marginBottom: 6, fontWeight: 500,
    }, { fontSize: 13, letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontWeight: 700 })}>{children}</div>
  );
}

function calNavBtnStyle() {
  return uiStyle({
    background: HR.surface, border: `1px solid ${HR.ruleSoft}`, borderRadius: 4,
    width: 32, height: 32, cursor: 'pointer', display: 'grid', placeItems: 'center',
    color: HR.inkSoft, padding: 0,
  }, {
    /* tonal icon button (36px, radius 10) */
    background: GxT.color.s2, border: 'none', borderRadius: GxT.radius.btnSm,
    width: 36, height: 36, color: GxT.color.onV, fontWeight: 700,
  });
}

function calIconBtnStyle() {
  return uiStyle({
    background: 'transparent', border: 'none', cursor: 'pointer', padding: 6,
    color: HR.inkSoft, borderRadius: 4, display: 'grid', placeItems: 'center',
  }, { borderRadius: GxT.radius.btnSm, minWidth: 36, minHeight: 36 });
}

function CalendarTab({
  employees, selectedEmpId, selectedEmpLabel, selectEmployee,
  departments, deptId, setDeptId, attendanceReloadKey,
  selfMode = false,
}) {
  const toast = useToast();
  // selfMode（一般員工）：只看自己的月曆，唯讀 —— 不顯示部門/員工挑選器，
  // Drawer 內補打卡/編輯/刪除/代補請假等 HR-only 操作全部隱藏（後端 HR-only，藏起來避免 403）。
  // 查詢 /attendance/calendar 不帶 employee_id（後端自動 resolve 本人）。
  const [selfEmp, setSelfEmp] = React.useState(null);
  const [selfResolved, setSelfResolved] = React.useState(false);

  React.useEffect(() => {
    if (!selfMode) return;
    api('GET', '/employees/me')
      .then(me => setSelfEmp(me || null))
      .catch(() => setSelfEmp(null))
      .finally(() => setSelfResolved(true));
  }, [selfMode]);

  // selfMode 用自己的員工檔；否則用跨 tab 共用的選擇
  const empId = selfMode ? (selfEmp?.id || null) : selectedEmpId;
  const empLabel = selfMode ? (selfEmp?.name || '') : selectedEmpLabel;
  const [yearMonth, setYearMonth] = React.useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });
  const [calendarData, setCalendarData] = React.useState(null);  // 從 /api/attendance/calendar
  const [loading, setLoading] = React.useState(false);
  const [drawerDate, setDrawerDate] = React.useState(null);
  const [drawerData, setDrawerData] = React.useState(null);  // 該日期的詳細資訊
  const [editTarget, setEditTarget] = React.useState(null);
  const [delTarget, setDelTarget] = React.useState(null);
  const [delBusy, setDelBusy] = React.useState(false);
  const [editLeaveTarget, setEditLeaveTarget] = React.useState(null);
  const [delLeaveTarget, setDelLeaveTarget] = React.useState(null);
  const [delLeaveBusy, setDelLeaveBusy] = React.useState(false);
  // 顯式的 reload 觸發器 — bump 才會讓 useEffect 重跑（同月編輯/刪除後用）
  const [reloadKey, setReloadKey] = React.useState(0);
  const reloadCalendar = React.useCallback(() => setReloadKey(k => k + 1), []);

  const confirmDeleteLeave = async () => {
    if (!delLeaveTarget) return;
    setDelLeaveBusy(true);
    try {
      await api('DELETE', `/leave/requests/${delLeaveTarget.id}`);
      toast('請假申請已取消/刪除', 'ok');
      setDelLeaveTarget(null);
      // 重新載入月曆
      reloadCalendar();
    } catch (e) {
      toast(e.detail || '刪除失敗', 'error');
    } finally {
      setDelLeaveBusy(false);
    }
  };

  // 拉月曆檢視數據：包含 attendance_daily + leave_requests
  React.useEffect(() => {
    // selfMode 尚未解析完自己的員工檔 → 等待（避免空查詢）
    if (selfMode && !selfResolved) return;
    // 無有效員工 → 清空（selfMode 即「帳號未綁員工」，非 selfMode 即「未選員工」）
    if (!empId) { setCalendarData(null); return; }
    setLoading(true);
    const qs = new URLSearchParams({
      year: String(yearMonth.y),
      month: String(yearMonth.m),
    });
    // selfMode 不帶 employee_id → 後端自動 resolve 本人
    if (!selfMode) qs.set('employee_id', String(selectedEmpId));
    api('GET', `/attendance/calendar?${qs.toString()}`)
      .then(r => setCalendarData(r))
      .catch(e => { toast(e.detail || '載入月曆失敗', 'error'); setCalendarData(null); })
      .finally(() => setLoading(false));
  }, [selfMode, selfResolved, empId, selectedEmpId, yearMonth.y, yearMonth.m, reloadKey, attendanceReloadKey]);

  // 生成月曆統計（從 calendarData）
  const monthStats = React.useMemo(() => {
    if (!calendarData) return { workdays: 0, punched: 0, onLeave: 0, totalWorkMins: 0 };
    let punched = 0, onLeave = 0, totalWorkMins = 0;
    for (const day of calendarData.days || []) {
      if (day.actual_in || day.actual_out) punched++;
      if (day.leave_id) onLeave++;
      totalWorkMins += (day.work_minutes || 0);
    }
    return { workdays: (calendarData.days || []).length, punched, onLeave, totalWorkMins };
  }, [calendarData]);

  const monthLabel = `${yearMonth.y} 年 ${yearMonth.m} 月`;
  const goPrevMonth = () => setYearMonth(({ y, m }) => m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 });
  const goNextMonth = () => setYearMonth(({ y, m }) => m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 });
  const goToday = () => {
    const d = new Date();
    setYearMonth({ y: d.getFullYear(), m: d.getMonth() + 1 });
  };

  const handlePickDate = (dateStr) => {
    setDrawerDate(dateStr);
    // 從 calendarData 找該日期的資訊
    const day = calendarData?.days?.find(d => d.date === dateStr);
    setDrawerData(day || null);
  };

  // 當 calendarData 重新載入後，同步更新打開中的 drawer 內容
  React.useEffect(() => {
    if (!drawerDate) return;
    const day = calendarData?.days?.find(d => d.date === drawerDate);
    setDrawerData(day || null);
  }, [calendarData, drawerDate]);

  const handleEdit = (p) => setEditTarget(p);
  const handleNew = () => {
    if (!selectedEmpId || !drawerDate) return;
    // 預設時間：套用該員工該 weekday 的 work_schedule；無排班時 fallback 09:00-17:30
    const schedIn = drawerData?.scheduled_in || '09:00';
    const schedOut = drawerData?.scheduled_out || '17:30';
    setEditTarget({
      employee_id: selectedEmpId,
      employee_name: selectedEmpLabel,
      punch_time: `${drawerDate}T${schedIn}:00+08:00`,
      abort_time: `${drawerDate}T${schedOut}:00+08:00`,
      remark: '',
    });
  };
  const handleDelete = (p) => setDelTarget(p);

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      await api('DELETE', `/attendance/punches/${delTarget.id}`);
      toast('已刪除', 'ok');
      setDelTarget(null);
      // 重新載入月曆
      reloadCalendar();
    } catch (e) {
      toast(e.detail || '刪除失敗', 'error');
    } finally {
      setDelBusy(false);
    }
  };

  const monthBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={goPrevMonth} title="上個月" style={calNavBtnStyle()}>
          <span style={{ display: 'inline-block', transform: 'rotate(180deg)' }}>
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        <div style={uiStyle({
          minWidth: 120, textAlign: 'center', fontFamily: HR.mono,
          fontSize: FontSizes.xs, fontWeight: 500,
        }, { fontFamily: GxT.font, fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' })}>{monthLabel}</div>
        <button onClick={goNextMonth} title="下個月" style={calNavBtnStyle()}>
          <Icon name="chevron-right" size={14} />
        </button>
        <button onClick={goToday} title="跳到本月"
          style={{ ...calNavBtnStyle(), padding: '0 12px', width: 'auto', fontSize: uiPick(FontSizes.xxs, 13), color: HR.ink }}>
          今天
        </button>
      </div>
    </div>
  );

  return (
    // Expressive: the tab becomes one borderless 16px card (picker strip on top, legend + grid inside)
    <div className={GxCore.cls(undefined, 'gx-att-cal-card')} style={uiPick(undefined, { background: GxT.color.card, borderRadius: GxT.radius.card })}>
      {selfMode ? (
        /* selfMode：不顯示部門/員工挑選器，只留月份導覽列 */
        <div style={uiStyle({
          display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
          padding: '14px 24px', borderBottom: `1px solid ${HR.ruleSoft}`, background: HR.surface,
        }, { padding: '14px 20px', background: 'transparent', borderRadius: `${GxT.radius.card}px ${GxT.radius.card}px 0 0` })}>
          <div style={uiStyle({ fontSize: FontSizes.xs, color: HR.inkSoft }, { fontSize: GxT.size.h3, fontWeight: 800, color: GxT.color.on })}>
            我的出勤月曆{empLabel ? ` — ${empLabel}` : ''}
            <span style={uiStyle({ color: HR.inkFaint, fontSize: FontSizes.trading_lg, marginLeft: 8 }, { fontWeight: 600 })}>（唯讀）</span>
          </div>
          <div style={{ marginLeft: 'auto' }}>{monthBar}</div>
        </div>
      ) : (
        <DeptEmployeePicker
          departments={departments}
          deptId={deptId}
          setDeptId={setDeptId}
          employees={employees}
          selectedEmpId={selectedEmpId}
          selectEmployee={selectEmployee}
          includeAll={false}
          right={monthBar}
        />
      )}
      <div className={GxCore.cls(undefined, 'gx-att-cal-body')} style={uiStyle({ padding: '20px 24px' }, { padding: '16px 20px 20px' })}>

      {/* 圖例列 */}
      <div style={uiStyle({
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
        background: HR.surfaceAlt, border: `1px solid ${HR.ruleSoft}`,
        borderRadius: 6, padding: '9px 16px', marginBottom: 14,
        fontSize: FontSizes.trading_lg,
      }, { gap: '8px 16px', border: 'none', borderRadius: GxT.radius.inner, padding: '10px 16px', fontSize: 12 })}>
        {(gxUiOn() ? [
          // Expressive: dots follow the GX palette (attendance = primary, leave = done/indigo, abnormal = error)
          { dot: GxT.color.p, label: '已打卡' },
          { dot: GxT.color.done, label: '請假' },
          { dot: GxT.color.down, label: '異常未打卡' },
        ] : [
          { dot: '#3B82C4', label: '已打卡' },
          { dot: '#8B5CF6', label: '請假' },
          { dot: '#9B3330', label: '異常未打卡' },
        ]).map(({ dot, label }) => (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: HR.inkSoft, fontWeight: uiPick(undefined, 600) }}>
            <span style={{ width: uiPick(7, 8), height: uiPick(7, 8), borderRadius: '50%', background: dot, flexShrink: 0 }} />
            {label}
          </span>
        ))}
        <span style={{ width: 1, height: 16, background: HR.ruleSoft }} />
        {(gxUiOn() ? [
          // Leave-type identity hues are kept; text shades are the AA-passing ones (same as the grid)
          { bg: '#FEE2E2', color: '#991B1B', label: '病假' },
          { bg: '#DBEAFE', color: '#1E40AF', label: '特休' },
          { bg: '#FEF3C7', color: '#92400E', label: '事假' },
          { bg: '#CCFBF1', color: '#115E59', label: '國定假日補休' },
          { bg: GxCore.tone('ok').bg, color: GxCore.tone('ok').fg, label: '國定假日' },
        ] : [
          { bg: '#FCECEA', color: '#9B3330', label: '病假' },
          { bg: '#EAF1FB', color: '#1a5fa8', label: '特休' },
          { bg: '#FDF3E4', color: '#A56A1F', label: '事假' },
          { bg: '#CCFBF1', color: '#0D9488', label: '國定假日補休' },
          { bg: '#CCFBF1', color: '#0D9488', label: '國定假日' },
        ]).map(({ bg, color, label }) => (
          <span key={label} style={uiStyle({
            display: 'inline-flex', alignItems: 'center',
            background: bg, color, border: `1px solid ${GxCore.alpha(color, '22')}`,
            padding: '2px 8px', borderRadius: 20,
            fontSize: FontSizes.trading_lg, fontWeight: 600,
          }, { border: 'none', borderRadius: GxT.radius.chipSm, padding: '2px 8px', fontSize: 12, fontWeight: 700 })}>{label}</span>
        ))}
        <span style={{ width: 1, height: 16, background: HR.ruleSoft }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: HR.inkSoft, fontWeight: uiPick(undefined, 600) }}>
          <span style={{ width: 14, height: 12, borderLeft: '3px solid #9B3330', borderRadius: 2, background: HR.surface, flexShrink: 0 }} />
          異常日（左框）
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: HR.inkSoft, fontWeight: uiPick(undefined, 600) }}>
          <span style={{
            width: 20, height: 20, borderRadius: '50%', background: HR.accent, color: '#fff',
            fontSize: FontSizes.trading_md, fontWeight: uiPick(600, 800),
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>今</span>
          今日
        </span>
      </div>

      {(selfMode ? (selfResolved && !empId) : !selectedEmpId) ? (
        <div style={uiStyle({
          padding: '80px 20px', textAlign: 'center', color: HR.inkFaint,
          background: HR.surface, border: `1px dashed ${HR.ruleSoft}`, borderRadius: 6,
        }, { padding: '64px 20px', background: GxT.color.s1, border: 'none', borderRadius: GxT.radius.inner, color: GxT.color.onV, fontWeight: 600 })}>
          {selfMode ? '您的帳號尚未綁定員工，請聯絡 HR。' : '請先選擇員工以查看月曆'}
        </div>
      ) : (
        <div className="hr-attendance-cal-outer">
          <AttendanceCalendarGrid
            calendarData={calendarData}
            loading={loading}
            onPickDate={handlePickDate}
            selectedDate={drawerDate}
          />
        </div>
      )}

      {drawerDate && empId && drawerData && (
        <DayDetailDrawerV2
          date={drawerDate}
          dayData={drawerData}
          employeeName={empLabel}
          employeeId={empId}
          onClose={() => setDrawerDate(null)}
          /* selfMode 唯讀：補打卡/編輯/刪除/代補請假等 HR-only 操作全部不掛（Drawer 依 props 有無自動隱藏按鈕） */
          onNew={selfMode ? null : handleNew}
          onEdit={selfMode ? null : handleEdit}
          onDelete={selfMode ? null : handleDelete}
          onLeaveNew={selfMode ? null : () => {
            const schedIn = drawerData?.scheduled_in || '09:00';
            const schedOut = drawerData?.scheduled_out || '18:00';
            setEditLeaveTarget({
              employee_id: selectedEmpId,
              employee_name: selectedEmpLabel,
              start_at: `${drawerDate}T${schedIn}`,
              end_at: `${drawerDate}T${schedOut}`,
              reason: '',
            });
          }}
          onLeaveDelete={selfMode ? null : ((t) => setDelLeaveTarget(t))}
        />
      )}

      {editTarget && (
        <PunchForm
          initial={editTarget}
          lockedDate={drawerDate}
          onCancel={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); reloadCalendar(); }}
        />
      )}

      {delTarget && (
        <Modal
          open onClose={delBusy ? null : () => setDelTarget(null)}
          title="刪除打卡紀錄"
          subtitle="Confirm Delete"
          size="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setDelTarget(null)} disabled={delBusy}>取消</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={delBusy}>{delBusy ? '刪除中…' : '確認刪除'}</Button>
          </>}
        >
          <div style={{ fontSize: FontSizes.xs, color: HR.inkSoft, lineHeight: 1.6 }}>
            確定要刪除這筆紀錄？<br />
            <span style={{ fontFamily: HR.mono, color: HR.ink, display: 'inline-block', marginTop: 6 }}>
              {isoToTPELocal(delTarget.punch_time)} → {delTarget.abort_time ? isoToTPEHHMM(delTarget.abort_time) : '—'}
            </span>
            <br />此動作無法復原。
          </div>
        </Modal>
      )}

      {editLeaveTarget && (
        <AdminLeaveRequestForm
          initial={editLeaveTarget}
          lockedDate={drawerDate}
          onCancel={() => setEditLeaveTarget(null)}
          onSaved={() => { setEditLeaveTarget(null); reloadCalendar(); }}
        />
      )}

      {delLeaveTarget && (
        <Modal
          open onClose={delLeaveBusy ? null : () => setDelLeaveTarget(null)}
          title="取消/刪除請假申請"
          subtitle="Confirm Delete Leave"
          size="sm"
          footer={<>
            <Button variant="ghost" onClick={() => setDelLeaveTarget(null)} disabled={delLeaveBusy}>取消</Button>
            <Button variant="danger" onClick={confirmDeleteLeave} disabled={delLeaveBusy}>{delLeaveBusy ? '刪除中…' : '確認刪除'}</Button>
          </>}
        >
          <div style={{ fontSize: FontSizes.xs, color: HR.inkSoft, lineHeight: 1.6 }}>
            確定要取消並刪除 {selectedEmpLabel} 的「{delLeaveTarget.leave_type_name}」請假申請？<br />
            <span style={{ fontFamily: HR.mono, color: HR.ink, display: 'inline-block', marginTop: 6 }}>
              日期：{delLeaveTarget.start_at}
            </span>
            <br />此操作將退還額度，且無法復原。
          </div>
        </Modal>
      )}
      </div>
    </div>
  );
}

function CalendarGrid({ year, month, punchesByDate, getScheduleForDate, loading, onPickDate, selectedDate }) {
  const firstDay = new Date(year, month - 1, 1);
  const firstDow = firstDay.getDay() === 0 ? 7 : firstDay.getDay();  // 1..7 (Mon..Sun)
  const lastDay = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 1; i < firstDow; i++) cells.push({ blank: true });
  for (let d = 1; d <= lastDay; d++) {
    cells.push({ date: ymdStr(year, month, d), day: d });
  }
  while (cells.length < 42) cells.push({ blank: true });

  const today = new Date();
  const todayDs = ymdStr(today.getFullYear(), today.getMonth() + 1, today.getDate());

  return (
    <div className="hr-attendance-cal-outer">
    <div className="hr-attendance-cal-inner" style={{
      background: HR.surface, border: `1px solid ${HR.ruleSoft}`, borderRadius: uiPick(6, GxT.radius.inner),
      overflow: 'hidden', position: 'relative',
    }}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)',
          display: 'grid', placeItems: 'center', zIndex: 10,
          fontSize: FontSizes.xxs, color: HR.inkSoft,
        }}>載入中…</div>
      )}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        background: HR.surfaceAlt, borderBottom: `1px solid ${HR.rule}`,
      }}>
        {['一','二','三','四','五','六','日'].map((w, i) => (
          <div key={w} style={{
            padding: '10px 12px', fontSize: FontSizes.xxs, fontWeight: 500,
            letterSpacing: '0.1em', textAlign: 'center',
            color: i >= 5 ? HR.inkFaint : HR.inkSoft,
          }}>{w}</div>
        ))}
      </div>
      <div className="hr-attendance-cal-cells" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((c, i) => {
          const colIsLast = i % 7 === 6;
          if (c.blank) {
            return (
              <div key={i} style={{
                minHeight: 110, background: HR.surfaceAlt,
                borderRight: colIsLast ? 'none' : `1px solid ${HR.ruleSoft}`,
                borderBottom: `1px solid ${HR.ruleSoft}`,
              }} />
            );
          }
          const dayPunches = punchesByDate.get(c.date) || [];
          const sched = getScheduleForDate(c.date);
          const status = computeDayStatus(dayPunches, sched);
          const palette = {
            normal:   { bg: '#E8F1EE', dot: '#0B5F5A' },
            abnormal: { bg: '#FCEFE3', dot: '#B45309' },
            rest:     { bg: '#F6F4EF', dot: HR.inkFaint },
            none:     { bg: HR.surface, dot: HR.inkFaint },
          }[status] || { bg: HR.surface, dot: HR.inkFaint };

          const isToday = c.date === todayDs;
          const isSelected = c.date === selectedDate;
          const first = dayPunches[0];

          return (
            <div
              key={i}
              onClick={() => onPickDate(c.date)}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.filter = 'brightness(0.97)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
              style={{
                aspectRatio: '3 / 2', padding: '8px 10px', overflow: 'hidden',
                borderRight: colIsLast ? 'none' : `1px solid ${HR.ruleSoft}`,
                borderBottom: `1px solid ${HR.ruleSoft}`,
                cursor: 'pointer',
                background: isSelected ? '#FFF8E8' : palette.bg,
                outline: isToday ? `2px solid ${HR.accent}` : 'none',
                outlineOffset: -2,
                position: 'relative',
                transition: 'filter 100ms',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{
                  fontSize: FontSizes.xs, fontWeight: isToday ? 600 : 500,
                  color: sched ? HR.ink : HR.inkFaint,
                  fontFamily: HR.mono,
                }}>{c.day}</span>
                {dayPunches.length > 0 && (
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', background: palette.dot,
                  }} />
                )}
              </div>
              {first && (
                <div style={{
                  marginTop: 6, fontSize: FontSizes.xxs, color: HR.inkSoft,
                  fontFamily: HR.mono, lineHeight: 1.5,
                }}>
                  <div>
                    {isoToTPEHHMM(first.punch_time)}
                    {first.abort_time
                      ? ` → ${isoToTPEHHMM(first.abort_time)}`
                      : <span style={{ color: '#B45309' }}> →—</span>}
                  </div>
                  {dayPunches.length > 1 && (
                    <div style={{ color: HR.inkFaint }}>+{dayPunches.length - 1} 段</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
}

function DayDetailDrawer({ date, employeeName, punches, breakMinutes = 0, onClose, onEdit, onNew, onDelete }) {
  // ESC 關閉
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rawTotalMin = punches.reduce((acc, p) => acc + (p.work_minutes || 0), 0);
  const netTotalMin = Math.max(0, rawTotalMin - (breakMinutes || 0));

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.35)', zIndex: 900,
      }} />
      <aside style={uiStyle({
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(440px, 92vw)',
        background: HR.bg, zIndex: 901,
        boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
      }, gxSideSheetPatch())}>
        <div style={uiStyle({ padding: '20px 24px', borderBottom: `1px solid ${HR.rule}`, background: HR.surface }, { borderBottom: 'none', background: 'transparent' })}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={uiStyle({
                fontSize: FontSizes.xxs, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: HR.inkFaint, marginBottom: 4,
              }, { fontSize: 13, fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: GxT.color.p })}>Day Detail</div>
              <h3 style={uiStyle({
                fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: 400,
                margin: 0, letterSpacing: '-0.015em',
              }, { fontSize: GxT.size.h2, fontWeight: 900 })}>{date}</h3>
              <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft, marginTop: 4 }}>
                {employeeName || '—'}
              </div>
            </div>
            <button onClick={onClose} title="關閉" style={calIconBtnStyle()}>
              <Icon name="x" size={18} />
            </button>
          </div>
          {punches.length > 0 && (
            <div style={{ marginTop: 12, fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono, lineHeight: 1.6 }}>
              共 {punches.length} 段 · 毛工時 {Math.floor(rawTotalMin / 60)}h {rawTotalMin % 60}m
              {breakMinutes > 0 && (
                <>
                  <br />
                  扣休息 {breakMinutes}m · <span style={{ color: HR.ink, fontWeight: 600 }}>實工時 {Math.floor(netTotalMin / 60)}h {netTotalMin % 60}m</span>
                </>
              )}
            </div>
          )}
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {punches.length === 0 ? (
            <div style={{
              padding: '40px 20px', textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs,
              border: `1px dashed ${HR.ruleSoft}`, borderRadius: uiPick(6, GxT.radius.inner), background: HR.surface,
            }}>
              當日無打卡紀錄<br />
              <span style={{ fontSize: FontSizes.xxs }}>點下方「補打卡」新增</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {punches.map(p => (
                <div key={p.id} style={uiStyle({
                  padding: '12px 14px', border: `1px solid ${HR.ruleSoft}`, borderRadius: 6,
                  background: HR.surface,
                }, { border: 'none', borderRadius: GxT.radius.inner })}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 500 }}>
                      {isoToTPEHHMM(p.punch_time)}
                      <span style={{ color: HR.inkFaint, margin: '0 6px' }}>→</span>
                      {p.abort_time
                        ? isoToTPEHHMM(p.abort_time)
                        : <span style={{ color: '#B45309' }}>未下班</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => onEdit(p)} title="編輯" style={calIconBtnStyle()}>
                        <Icon name="edit" size={14} />
                      </button>
                      <button onClick={() => onDelete(p)} title="刪除" style={calIconBtnStyle()}>
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  </div>
                  {p.remark && (
                    <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft, marginTop: 6 }}>{p.remark}</div>
                  )}
                  {p.work_minutes != null && (
                    <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, marginTop: 4, fontFamily: HR.mono }}>
                      工時 {Math.floor(p.work_minutes / 60)}h {p.work_minutes % 60}m
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={uiStyle({ padding: '14px 24px', borderTop: `1px solid ${HR.rule}`, background: HR.surfaceAlt }, { borderTop: 'none', background: 'transparent' })}>
          <Button variant="primary" icon="plus" onClick={onNew}>補打卡</Button>
        </div>
      </aside>
    </>
  );
}

/* ─────────────────────────  New: Attendance Calendar (v2)  ────────────── */

function AttendanceCalendarGrid({ calendarData, loading, onPickDate, selectedDate }) {
  if (!calendarData) return null;

  const { year, month, days } = calendarData;
  const firstDay = new Date(year, month - 1, 1);
  const firstDow = firstDay.getDay();  // 0=Sun..6=Sat
  const cells = [];

  // 轉換為 ISO weekday (1=Mon..7=Sun)，計算空位數
  // getDay(): 0=Sun, 1=Mon, 2=Tue, ..., 6=Sat
  // 需要填充的空位：0(Sun)→6, 1(Mon)→0, 2(Tue)→1, ..., 6(Sat)→5
  const offsetDow = firstDow === 0 ? 6 : firstDow - 1;
  for (let i = 0; i < offsetDow; i++) cells.push({ blank: true });

  // 填入每月的日期
  for (const day of days) {
    cells.push({ ...day });
  }

  // 填補月末後的空位
  while (cells.length % 7 !== 0) cells.push({ blank: true });

  const today = new Date();
  const todayDs = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // 請假類型顏色映射
  // Expressive keeps each leave type's hue (identity colour) but uses the darker, AA-passing text
  // shade (the same shades DayDetailDrawerV2 already uses); the Bento map is untouched.
  const leaveColors = uiPick({
    sick: { bg: '#FEE2E2', text: '#DC2626' },      // 病假 - 紅
    annual: { bg: '#DBEAFE', text: '#0284C7' },    // 特休 - 藍
    personal: { bg: '#FEF3C7', text: '#D97706' },  // 事假 - 黃
    unpaid: { bg: '#F3E8FF', text: '#A855F7' },    // 無薪假 - 紫
    holiday_com: { bg: '#CCFBF1', text: '#0D9488' }, // 國定假日補休 - 青綠
    other: { bg: '#E5E7EB', text: '#6B7280' },     // 其他 - 灰
  }, {
    sick: { bg: '#FEE2E2', text: '#991B1B' },
    annual: { bg: '#DBEAFE', text: '#1E40AF' },
    personal: { bg: '#FEF3C7', text: '#92400E' },
    unpaid: { bg: '#F3E8FF', text: '#6B21A8' },
    holiday_com: { bg: '#CCFBF1', text: '#115E59' },
    other: { bg: '#E5E7EB', text: '#374151' },
  });
  const gc = GxT.color;
  const holidayTone = GxCore.tone('ok');

  return (
    <div className="hr-attendance-cal-inner" style={uiStyle({
      background: HR.rule, border: `1px solid ${HR.rule}`, borderRadius: 10,
      overflow: 'hidden', position: 'relative',
      boxShadow: '0 1px 3px rgba(14,20,17,.06), 0 1px 2px rgba(14,20,17,.04)',
    }, { background: gc.s3, border: `1px solid ${gc.s3}`, borderRadius: GxT.radius.inner, boxShadow: 'none' })}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)',
          display: 'grid', placeItems: 'center', zIndex: 10,
          fontSize: FontSizes.xxs, color: HR.inkSoft,
        }}>載入中…</div>
      )}

      {/* 週標頭 */}
      <div style={uiStyle({
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        background: HR.surfaceAlt, borderBottom: `1px solid ${HR.rule}`,
      }, { background: gc.s2, borderBottom: `1px solid ${gc.s3}` })}>
        {['一','二','三','四','五','六','日'].map((w, i) => (
          <div key={w} style={uiStyle({
            padding: '10px 12px', fontSize: FontSizes.xxs, fontWeight: 500,
            letterSpacing: '0.1em', textAlign: 'center',
            color: i >= 5 ? HR.inkFaint : HR.inkSoft,
          }, { fontWeight: 700, letterSpacing: 0, fontSize: 12 })}>週{w}</div>
        ))}
      </div>

      {/* 日期格子 */}
      <div className="hr-attendance-cal-cells" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((c, i) => {
          const colIsLast = i % 7 === 6;

          if (c.blank) {
            return (
              <div key={i} style={{
                minHeight: 110, background: HR.surfaceAlt,
                borderRight: colIsLast ? 'none' : `1px solid ${HR.ruleSoft}`,
                borderBottom: `1px solid ${HR.ruleSoft}`,
              }} />
            );
          }

          const isToday = c.date === todayDs;
          const isSelected = c.date === selectedDate;
          const hasAttendance = c.actual_in || c.actual_out;
          // 同日可多筆不重疊假別；舊單數欄位作為 fallback
          const dayLeaves = (c.leaves && c.leaves.length)
            ? c.leaves
            : (c.leave_id ? [{
                leave_id: c.leave_id, leave_type: c.leave_type,
                leave_type_name: c.leave_type_name,
                leave_start_at: c.leave_start_at, leave_end_at: c.leave_end_at,
              }] : []);
          const hasLeave = dayLeaves.length > 0;
          // 異常 = 工作天 + 無打卡 + 無請假（後端已過濾掉今天/未來/在職範圍外）
          const isAbnormal = c.is_workday && !hasAttendance && !hasLeave;
          // 週末偵測 — 0=Sun, 6=Sat
          const dow = new Date(c.date + 'T00:00:00').getDay();
          const isWeekend = dow === 0 || dow === 6;

          const baseBg = isAbnormal ? '#FFFBFB' : isWeekend ? uiPick('#FDFCF8', gc.s1) : HR.surface;
          const bgColor = isSelected ? uiPick('#E6F5F3', gc.pc) : isToday ? '#F2FAF9' : baseBg;

          return (
            <div
              key={i}
              onClick={() => onPickDate(c.date)}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#FAFAFA'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = bgColor; }}
              style={uiStyle({
                minHeight: 118, padding: '8px 10px', overflow: 'hidden',
                borderRight: colIsLast ? 'none' : `1px solid ${HR.ruleSoft}`,
                borderBottom: `1px solid ${HR.ruleSoft}`,
                cursor: 'pointer',
                background: bgColor,
                borderLeft: isAbnormal ? '3px solid #9B3330' : undefined,
                position: 'relative',
                transition: 'background 100ms',
              }, isSelected ? { boxShadow: `inset 0 0 0 2px ${gc.p}` } : {})}
            >
              {/* 日期 + 圓點 indicator */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                marginBottom: 5,
              }}>
                {(hasAttendance || hasLeave || isAbnormal) && (
                  <div style={{ display: 'flex', gap: 2 }}>
                    {hasAttendance && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: uiPick('#3B82F6', gc.p) }} />
                    )}
                    {hasLeave && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: uiPick('#A855F7', gc.done) }} />
                    )}
                    {isAbnormal && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: uiPick('#DC2626', gc.down) }} />
                    )}
                  </div>
                )}
                <span style={uiStyle({
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: '50%',
                  fontSize: FontSizes.xxs, fontFamily: HR.mono,
                  fontWeight: isToday ? 600 : 400,
                  color: isToday ? '#fff' : HR.ink,
                  background: isToday ? HR.accent : 'transparent',
                  marginBottom: 4, flexShrink: 0,
                }, {
                  width: 26, height: 26, fontSize: 13, fontFamily: GxT.font, fontVariantNumeric: 'tabular-nums',
                  fontWeight: isToday ? 800 : 600, color: isToday ? gc.onP : gc.on,
                  background: isToday ? gc.p : 'transparent',
                })}>
                  {c.date.split('-')[2]}
                </span>
              </div>

              {/* 異常文字 */}
              {isAbnormal && (
                <div style={{
                  fontSize: FontSizes.xxs, color: uiPick('#DC2626', gc.down), fontWeight: uiPick(600, 700),
                  marginLeft: 14,
                }}>⚠ 未打卡</div>
              )}

              {/* 國定假日標記 — 即使該員工當天無排班也顯示（青綠色，與補休一致） */}
              {c.is_holiday && (
                <div style={uiStyle({
                  display: 'inline-flex', alignItems: 'center',
                  background: '#CCFBF1', color: '#0D9488',
                  padding: '2px 7px', borderRadius: 3,
                  fontSize: FontSizes.xxs, fontWeight: 600,
                  lineHeight: 1.3, marginLeft: 14,
                }, { background: holidayTone.bg, color: holidayTone.fg, borderRadius: GxT.radius.chipSm, fontWeight: 700 })}>國定假日</div>
              )}

              {/* 打卡時間 — 時鐘 icon + accent 綠，明確標示為「工作時段」 */}
              {hasAttendance && c.actual_in && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: FontSizes.xxs, color: HR.accent, fontWeight: 500,
                  fontFamily: HR.mono, lineHeight: 1.4,
                  marginLeft: 14,
                }}>
                  <Icon name="clock" size={11} stroke={2} />
                  <span>
                    {fmtTime(c.actual_in)}
                    {c.actual_out ? ` → ${fmtTime(c.actual_out)}` : ' → —'}
                  </span>
                </div>
              )}

              {/* 當日工時 */}
              {hasAttendance && c.work_minutes > 0 && (
                <div style={{
                  marginLeft: 14, marginTop: 2,
                  fontSize: FontSizes.xxs, color: HR.inkSoft,
                  fontFamily: HR.mono, lineHeight: 1.4,
                }}>
                  {fmtDuration(c.work_minutes)}
                </div>
              )}

              {/* 假別 chip — 同日多筆不重疊假別各一顆彩色 chip（顏色依假別 code） */}
              {hasLeave && (
                <div style={{ marginTop: hasAttendance ? 4 : 2, marginLeft: 14, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {dayLeaves.map((lv, li) => {
                    const lc = leaveColors[lv.leave_type] || leaveColors.other;
                    return (
                      <div key={lv.leave_id ?? li} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: lc.bg, color: lc.text,
                        padding: '2px 7px', borderRadius: uiPick(3, GxT.radius.chipSm),
                        fontSize: FontSizes.xxs, fontWeight: uiPick(600, 700),
                        lineHeight: 1.3, alignSelf: 'flex-start',
                      }}>
                        <span>{lv.leave_type_name || lv.leave_type}</span>
                        {lv.leave_start_at && lv.leave_end_at && (
                          <>
                            <span style={{ opacity: 0.45, fontWeight: 400 }}>·</span>
                            <span style={{ fontFamily: HR.mono, fontWeight: 400, fontSize: FontSizes.xxs }}>
                              {lv.leave_start_at}–{lv.leave_end_at}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayDetailDrawerV2({ date, dayData, employeeName, employeeId, onClose, onNew, onEdit, onDelete, onLeaveNew, onLeaveDelete }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const leaveColors = {
    sick: { bg: '#FEE2E2', text: '#991B1B' },
    annual: { bg: '#DBEAFE', text: '#1E40AF' },
    personal: { bg: '#FEF3C7', text: '#92400E' },
    unpaid: { bg: '#F3E8FF', text: '#6B21A8' },
    holiday_com: { bg: '#CCFBF1', text: uiPick('#0D9488', '#115E59') }, // 國定假日補休 - 青綠（code 為 holiday_com，非 holiday_comp）；GX 用 AA 深色字
    other: { bg: '#E5E7EB', text: '#374151' },
  };
  const gc = GxT.color;
  const gxCardPatch = { border: 'none', borderRadius: GxT.radius.inner, background: gc.card };
  const gxSmallIconBtn = { width: 32, height: 32, border: '1px solid transparent', borderRadius: GxT.radius.chip };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.35)', zIndex: 900,
      }} />
      <aside role={uiPick(undefined, 'dialog')} aria-label={uiPick(undefined, `${employeeName || '員工'} ${date}`)} style={uiStyle({
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(440px, 92vw)',
        background: HR.bg, zIndex: 901,
        boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
      }, gxSideSheetPatch())}>
        <div style={uiStyle({ padding: '18px 20px 14px', borderBottom: `1px solid ${HR.rule}`, background: HR.surface, flexShrink: 0 }, { padding: '20px 20px 8px', borderBottom: 'none', background: 'transparent' })}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, color: HR.ink, fontWeight: 400, letterSpacing: '-0.01em' }, { fontSize: GxT.size.h2, fontWeight: 900, letterSpacing: '-0.3px', lineHeight: 1.2 })}>
              {employeeName || '員工'}
            </span>
            <button onClick={onClose} title="關閉" aria-label={uiPick(undefined, '關閉')} style={uiStyle({
              width: 28, height: 28, border: `1px solid ${HR.rule}`, borderRadius: 4,
              background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: HR.inkFaint, fontSize: 16, lineHeight: 1, transition: 'background .12s, color .12s',
            }, { width: 40, height: 40, border: '1px solid transparent', borderRadius: GxT.radius.btn, color: gc.onV, fontSize: 18 })}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#FCECEA'; e.currentTarget.style.color = '#9B3330'; e.currentTarget.style.borderColor = '#9B3330'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HR.inkFaint; e.currentTarget.style.borderColor = uiPick(HR.rule, 'transparent'); }}
            >✕</button>
          </div>
          <div style={uiStyle({ fontSize: FontSizes.xs, color: HR.inkSoft }, { fontSize: 13, fontWeight: 700, color: gc.p, fontVariantNumeric: 'tabular-nums' })}>
            {date}
            {(() => {
              const wd = ['日','一','二','三','四','五','六'][new Date(date + 'T00:00:00').getDay()];
              return <span style={uiStyle({ color: HR.inkFaint, fontSize: FontSizes.trading_lg, marginLeft: 6 }, { color: gc.p })}>（週{wd}）</span>;
            })()}
          </div>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* 排班資訊 */}
          {(dayData.scheduled_in || dayData.scheduled_out) && (
            <div>
              <div style={uiStyle({ fontSize: FontSizes.trading_lg, fontWeight: 600, color: HR.inkFaint, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }, gxSheetSectionLabelPatch())}>
                排班
              </div>
              <div style={uiStyle({
                background: HR.surfaceAlt, border: `1px solid ${HR.ruleSoft}`, borderRadius: 8,
                padding: '11px 14px',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px',
              }, { ...gxCardPatch, padding: '12px 16px' })}>
                {[
                  { key: '上班', val: dayData.scheduled_in || '—' },
                  { key: '下班', val: dayData.scheduled_out || '—' },
                  { key: '午休', val: dayData.break_minutes != null ? `${dayData.break_minutes}m` : '—' },
                  { key: '應到', val: (() => {
                    if (!dayData.scheduled_in || !dayData.scheduled_out) return '—';
                    const [sh, sm] = dayData.scheduled_in.split(':').map(Number);
                    const [eh, em] = dayData.scheduled_out.split(':').map(Number);
                    const mins = (eh * 60 + em) - (sh * 60 + sm) - (dayData.break_minutes || 0);
                    return fmtDuration(mins);
                  })() },
                ].map(({ key, val }) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={uiStyle({ fontSize: FontSizes.trading_md, color: HR.inkFaint, textTransform: 'uppercase', letterSpacing: '0.06em' }, { fontSize: 12, fontWeight: 600, textTransform: 'none', letterSpacing: 0 })}>{key}</span>
                    <span style={uiStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs, color: HR.ink, fontWeight: 500 }, { fontFamily: GxT.font, fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' })}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 打卡資訊 — 列出該日所有 punch_records，可編輯/刪除 */}
          {/* 打卡紀錄 — punch cards */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8,
            }}>
              <div style={uiStyle({ fontSize: FontSizes.trading_lg, fontWeight: 600, color: HR.inkFaint, textTransform: 'uppercase', letterSpacing: '0.08em' }, gxSheetSectionLabelPatch())}>
                打卡紀錄
              </div>
              {(dayData.punches && dayData.punches.length > 1) && (
                <div style={uiStyle({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }, { fontFamily: GxT.font, fontWeight: 600 })}>
                  共 {dayData.punches.length} 段
                </div>
              )}
            </div>
            {(dayData.punches && dayData.punches.length > 0) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dayData.punches.map(p => (
                  <div key={p.id} style={uiStyle({
                    padding: '11px 12px', border: `1px solid ${HR.rule}`, borderRadius: 8,
                    background: HR.surface, display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'box-shadow .12s',
                  }, { ...gxCardPatch, padding: '12px 12px 12px 14px' })}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(14,20,17,.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {/* 時鐘 icon */}
                    <div style={{
                      width: 32, height: 32, borderRadius: uiPick(6, GxT.radius.btnSm),
                      background: HR.accentSoft, color: HR.accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, fontSize: 15,
                    }}>⏰</div>
                    {/* 時間 */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={uiStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 500, color: HR.ink }, { fontFamily: GxT.font, fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' })}>
                        {fmtTime(p.punch_time)}
                        <span style={{ color: HR.inkFaint, margin: '0 5px' }}>→</span>
                        {p.abort_time
                          ? fmtTime(p.abort_time)
                          : <span style={{ color: '#B45309' }}>進行中</span>}
                      </div>
                      {p.remark && (
                        <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 2 }}>{p.remark}</div>
                      )}
                    </div>
                    {/* 工時 badge */}
                    {p.work_minutes != null && (
                      <span style={uiStyle({
                        fontFamily: HR.mono, fontSize: FontSizes.trading_lg, fontWeight: 500,
                        color: HR.accent, background: HR.accentSoft,
                        padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap',
                      }, { fontFamily: GxT.font, fontSize: 12, fontWeight: 700, color: gc.onPc, background: gc.pc, borderRadius: GxT.radius.chip, fontVariantNumeric: 'tabular-nums' })}>{fmtDuration(p.work_minutes)}</span>
                    )}
                    {/* 操作 */}
                    {(onEdit || onDelete) && (
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {onEdit && (
                          <button onClick={() => onEdit({ ...p, employee_id: employeeId, employee_name: employeeName })}
                            title="編輯" aria-label={uiPick(undefined, '編輯')} style={uiStyle({
                              width: 26, height: 26, border: `1px solid ${HR.ruleSoft}`, borderRadius: 4,
                              background: 'transparent', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: HR.inkFaint, transition: 'background .12s, color .12s, border-color .12s',
                            }, gxSmallIconBtn)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = HR.accentSoft; e.currentTarget.style.color = HR.accent; e.currentTarget.style.borderColor = HR.accent; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HR.inkFaint; e.currentTarget.style.borderColor = uiPick(HR.ruleSoft, 'transparent'); }}
                          ><Icon name="edit" size={13} /></button>
                        )}
                        {onDelete && (
                          <button onClick={() => onDelete(p)} title="刪除" aria-label={uiPick(undefined, '刪除')} style={uiStyle({
                            width: 26, height: 26, border: `1px solid ${HR.ruleSoft}`, borderRadius: 4,
                            background: 'transparent', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: HR.inkFaint, transition: 'background .12s, color .12s, border-color .12s',
                          }, gxSmallIconBtn)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FCECEA'; e.currentTarget.style.color = '#9B3330'; e.currentTarget.style.borderColor = '#9B3330'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HR.inkFaint; e.currentTarget.style.borderColor = uiPick(HR.ruleSoft, 'transparent'); }}
                          ><Icon name="trash" size={13} /></button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={uiStyle({
                padding: '18px 14px', textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xxs,
                border: `1.5px dashed ${HR.ruleSoft}`, borderRadius: 8, background: HR.surface,
              }, { borderRadius: GxT.radius.inner, border: `1.5px dashed ${gc.s4}`, fontSize: 13 })}>
                無打卡紀錄
              </div>
            )}
            {dayData.late_minutes > 0 && (
              <div style={uiStyle({ fontSize: FontSizes.trading_lg, color: '#B45309', fontFamily: HR.mono, marginTop: 8 }, {
                display: 'inline-flex', fontSize: 12, fontWeight: 700, fontFamily: GxT.font,
                color: GxCore.tone('warn').fg, background: GxCore.tone('warn').bg, borderRadius: GxT.radius.chipSm, padding: '3px 8px',
              })}>
                ⚠ 遲到 {dayData.late_minutes} 分鐘
              </div>
            )}
          </div>

          {/* 請假資訊 — 同日可多筆不重疊假別 */}
          {(() => {
            const drawerLeaves = (dayData.leaves && dayData.leaves.length)
              ? dayData.leaves
              : (dayData.leave_id ? [{
                  leave_id: dayData.leave_id, leave_type: dayData.leave_type,
                  leave_type_name: dayData.leave_type_name, leave_status: dayData.leave_status,
                  leave_start_at: dayData.leave_start_at, leave_end_at: dayData.leave_end_at,
                  leave_hours: dayData.leave_hours, leave_reason: dayData.leave_reason,
                }] : []);
            if (!drawerLeaves.length) return null;
            return (
              <div>
                <div style={uiStyle({ fontSize: FontSizes.xxs, fontWeight: 600, color: HR.inkSoft, marginBottom: 8, textTransform: 'uppercase' }, gxSheetSectionLabelPatch())}>
                  請假資訊{drawerLeaves.length > 1 ? `（${drawerLeaves.length} 筆）` : ''}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {drawerLeaves.map((lv, li) => (
                    <div key={lv.leave_id ?? li} style={uiStyle({
                      padding: '12px 14px', border: `1px solid ${HR.ruleSoft}`, borderRadius: 6,
                      background: HR.surface,
                    }, gxCardPatch)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{
                          fontSize: FontSizes.xxs, fontWeight: uiPick(600, 700),
                          background: leaveColors[lv.leave_type]?.bg || leaveColors.other.bg,
                          color: leaveColors[lv.leave_type]?.text || leaveColors.other.text,
                          padding: '4px 8px', borderRadius: uiPick(4, GxT.radius.chipSm),
                        }}>
                          {lv.leave_type_name || lv.leave_type}
                        </span>
                        <span style={uiStyle({
                          fontSize: FontSizes.xxs, color: HR.inkFaint,
                          background: HR.surfaceAlt, padding: '3px 8px', borderRadius: 3,
                        }, { color: gc.onV, background: gc.s2, borderRadius: GxT.radius.chipSm, fontWeight: 600 })}>
                          {lv.leave_status}
                        </span>
                        {onLeaveDelete && (
                          <button
                            onClick={() => onLeaveDelete({ id: lv.leave_id, leave_type_name: lv.leave_type_name || lv.leave_type, start_at: date })}
                            title="取消並刪除此請假"
                            aria-label={uiPick(undefined, '取消並刪除此請假')}
                            style={uiStyle({
                              marginLeft: 'auto',
                              width: 26, height: 26, border: `1px solid ${HR.ruleSoft}`, borderRadius: 4,
                              background: 'transparent', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: HR.inkFaint, transition: 'background .12s, color .12s, border-color .12s',
                            }, gxSmallIconBtn)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#FCECEA'; e.currentTarget.style.color = '#9B3330'; e.currentTarget.style.borderColor = '#9B3330'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HR.inkFaint; e.currentTarget.style.borderColor = uiPick(HR.ruleSoft, 'transparent'); }}
                          >✕</button>
                        )}
                      </div>
                      {lv.leave_start_at && lv.leave_end_at && (
                        <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft, marginBottom: 6, fontFamily: HR.mono }}>
                          時段：{lv.leave_start_at} – {lv.leave_end_at}
                        </div>
                      )}
                      {lv.leave_hours != null && (
                        <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft, marginBottom: 6, fontFamily: HR.mono }}>
                          時數：{lv.leave_hours} 小時
                        </div>
                      )}
                      {lv.leave_reason && (
                        <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                          事由：{lv.leave_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* 操作提示只在可操作（非唯讀）時顯示 */}
                {onLeaveDelete && (
                  <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 8 }}>
                    💡 已核准假單不可直接修改。若需調整請點該筆右上角「✕」刪除後重新申請。同日要再加一筆，請點下方「代補請假」並把時段改成與既有不重疊。
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {onNew && (
          <div style={uiStyle({ padding: '12px 20px', borderTop: `1px solid ${HR.rule}`, background: HR.surfaceAlt, flexShrink: 0, display: 'flex', gap: 10 }, { padding: '12px 20px 20px', borderTop: 'none', background: 'transparent' })}>
            <button onClick={onNew} style={uiStyle({
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              flex: 1, padding: '10px 16px',
              border: `1.5px dashed ${HR.rule}`, borderRadius: 8,
              background: 'transparent', color: HR.inkSoft,
              fontFamily: HR.sans, fontSize: FontSizes.xs, cursor: 'pointer',
              transition: 'border-color .12s, background .12s, color .12s',
            }, { minHeight: 48, border: `1.5px dashed ${gc.ol}`, borderRadius: GxT.radius.btn, fontSize: 14, fontWeight: 700 })}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.background = HR.accentSoft; e.currentTarget.style.color = HR.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = uiPick(HR.rule, gc.ol); e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HR.inkSoft; }}
            >
              <span style={{
                width: 18, height: 18, borderRadius: '50%', background: HR.ruleSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, transition: 'background .12s',
              }}>＋</span>
              補打卡紀錄
            </button>
            {onLeaveNew && (
              <button onClick={onLeaveNew} style={uiStyle({
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                flex: 1, padding: '10px 16px',
                border: `1.5px dashed ${HR.rule}`, borderRadius: 8,
                background: 'transparent', color: HR.inkSoft,
                fontFamily: HR.sans, fontSize: FontSizes.xs, cursor: 'pointer',
                transition: 'border-color .12s, background .12s, color .12s',
              }, { minHeight: 48, border: `1.5px dashed ${gc.ol}`, borderRadius: GxT.radius.btn, fontSize: 14, fontWeight: 700 })}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8B5CF6'; e.currentTarget.style.background = '#F5F3FF'; e.currentTarget.style.color = '#8B5CF6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = uiPick(HR.rule, gc.ol); e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = HR.inkSoft; }}
              >
                <span style={{
                  width: 18, height: 18, borderRadius: '50%', background: HR.ruleSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, transition: 'background .12s',
                }}>＋</span>
                代補請假
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

// TimeSelect（時間下拉選擇器）已搬到 shared.jsx —— 補打卡 modal 與請假表單（hr-leave.jsx）共用。

function AdminLeaveRequestForm({ initial, onCancel, onSaved, lockedDate = null }) {
  const toast = useToast();
  // 鎖定日期模式：優先用傳入的 lockedDate，否則退而取 initial.start_at 的日期部分。
  // 鎖定時日期固定、只能改 HH:MM；組出的 'YYYY-MM-DDTHH:MM' 與舊格式相同，後端視為台北時區。
  const fixedDate = lockedDate || (initial?.start_at ? initial.start_at.split('T')[0] : null);
  const locked = !!fixedDate;
  const _timePart = (s) => (s && s.includes('T')) ? s.split('T')[1].slice(0, 5) : '';

  const [types, setTypes] = React.useState([]);
  const [loadingTypes, setLoadingTypes] = React.useState(true);
  const [typeId, setTypeId] = React.useState(null);
  // 一般模式存完整 datetime-local 字串；鎖定模式存 HH:MM
  const [from, setFrom] = React.useState(locked ? '' : (initial?.start_at || ''));
  const [to, setTo] = React.useState(locked ? '' : (initial?.end_at || ''));
  const [fromTime, setFromTime] = React.useState(() => locked ? _timePart(initial?.start_at) : '');
  const [toTime, setToTime] = React.useState(() => locked ? _timePart(initial?.end_at) : '');
  const [reason, setReason] = React.useState(initial?.reason || '');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    api('GET', '/leave/types?active_only=true')
      .then(res => {
        const activeTypes = Array.isArray(res) ? res : [];
        setTypes(activeTypes);
        if (activeTypes.length > 0) {
          setTypeId(activeTypes[0].id);
        }
      })
      .catch(e => {
        toast('載入假別失敗：' + (e.detail || ''), 'error');
      })
      .finally(() => {
        setLoadingTypes(false);
      });
  }, []);

  const selectedType = types.find(t => t.id === typeId);

  // 快捷時段（鎖定模式）：整天 = 排班起訖；上午半 = 排班起~12:00；下午半 = 13:00~排班迄。
  // 排班起訖取自 initial（onLeaveNew 帶當天排班時段）；午休邊界採系統慣例 12:00/13:00。
  const workStart = _timePart(initial?.start_at) || '09:00';
  const workEnd = _timePart(initial?.end_at) || '18:00';
  const presets = [
    { key: 'full', label: '整天', from: workStart, to: workEnd },
    { key: 'am', label: '上午半', from: workStart, to: '12:00' },
    { key: 'pm', label: '下午半', from: '13:00', to: workEnd },
  ];
  const applyPreset = (p) => { setFromTime(p.from); setToTime(p.to); };
  const activePreset = presets.find(p => p.from === fromTime && p.to === toTime)?.key;

  // 實際送出的 start_at / end_at（鎖定模式由 fixedDate + HH:MM 組成）
  const startAt = locked ? (fromTime ? `${fixedDate}T${fromTime}` : '') : from;
  const endAt   = locked ? (toTime   ? `${fixedDate}T${toTime}`   : '') : to;

  const startMs = new Date(startAt).getTime();
  const endMs = new Date(endAt).getTime();
  const diffHours = endMs > startMs ? (endMs - startMs) / 3600000 : 0;
  const diffDays = diffHours / 8;

  const submit = async () => {
    if (!typeId) { toast('請選擇假別', 'error'); return; }
    if (!startAt || !endAt) { toast(locked ? '請選擇開始與結束時間' : '請填寫起訖時間', 'error'); return; }
    if (endMs <= startMs) { toast('結束時間需晚於開始時間', 'error'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/leave/requests', {
        employee_id: initial.employee_id,
        leave_type_id: typeId,
        start_at: startAt,
        end_at: endAt,
        hours: null,
        reason: reason.trim() || null,
        proof_file_path: null,
      });
      toast('請假申請已送出（待審核）', 'ok');
      onSaved();
    } catch (e) {
      toast('送出失敗：' + (e.detail || e.message || ''), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="代補請假"
      subtitle={`員工：${initial.employee_name}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting || loadingTypes}>
            {submitting ? '送出中…' : '確認送出'}
          </Button>
        </>
      }
    >
      {locked && (
        <div style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', marginBottom: 14,
          background: '#E6EEED', border: `1px solid #B7CFC9`, borderRadius: 6,
          fontSize: FontSizes.xs, color: '#0B5F5A',
        }, gxLockedHintPatch())}>
          <Icon name="calendar" size={15} />
          <span>日期已鎖定為 <strong style={{ fontFamily: HR.mono }}>{fixedDate}</strong>，只能調整時間</span>
        </div>
      )}
      <div className="hr-leave-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="假別" required span={2}>
          {loadingTypes ? (
            <div style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>載入假別中…</div>
          ) : (
            <select
              value={typeId || ''}
              onChange={(e) => setTypeId(Number(e.target.value))}
              style={inputStyle()}
            >
              {types.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.code === 'holiday_comp' ? ' (管理員專屬)' : ''}
                </option>
              ))}
            </select>
          )}
        </Field>
        {locked ? (
          <>
            <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={uiStyle({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' }, gxPresetLabelPatch())}>快捷</span>
              {presets.map(p => {
                const on = activePreset === p.key;
                return (
                  <button key={p.key} type="button" onClick={() => applyPreset(p)}
                    aria-pressed={uiPick(undefined, on)}
                    style={uiStyle({
                      padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
                      fontSize: FontSizes.xxs, fontWeight: 600,
                      border: `1px solid ${on ? HR.accent : HR.rule}`,
                      background: on ? HR.accentSoft : HR.surface,
                      color: on ? HR.accent : HR.inkSoft,
                      transition: 'background .12s, border-color .12s, color .12s',
                    }, gxPresetChipPatch(on))}
                    onMouseEnter={(e) => { if (!on) { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.color = HR.accent; } }}
                    onMouseLeave={(e) => { if (!on) { e.currentTarget.style.borderColor = HR.rule; e.currentTarget.style.color = HR.inkSoft; } }}
                  >{p.label}</button>
                );
              })}
            </div>
            <Field label="開始時間 (台北時區)" required>
              <TimeSelect value={fromTime} onChange={setFromTime} />
            </Field>
            <Field label="結束時間 (台北時區)" required>
              <TimeSelect value={toTime} onChange={setToTime} />
            </Field>
          </>
        ) : (
          <>
            <Field label="開始時間" required hint="精確到分鐘">
              <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle()} />
            </Field>
            <Field label="結束時間" required hint="精確到分鐘">
              <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle()} />
            </Field>
          </>
        )}
        <Field label="事由" hint="請假事由" span={2}>
          <Textarea value={reason} onChange={setReason} rows={3} placeholder="請輸入事由…" />
        </Field>

        <div style={uiStyle({
          gridColumn: 'span 2', padding: 12, background: HR.surfaceAlt, borderRadius: 4,
          fontSize: FontSizes.xxs, color: HR.inkSoft, fontFamily: HR.mono, lineHeight: 1.6,
        }, { padding: '12px 14px', background: GxT.color.s2, borderRadius: GxT.radius.inner, fontSize: 13, fontFamily: GxT.font, fontVariantNumeric: 'tabular-nums' })}>
          時長估算：<strong style={{ color: HR.ink }}>{diffHours.toFixed(2)}</strong> 小時 · 約 <strong style={{ color: HR.ink }}>{diffDays.toFixed(2)}</strong> 天 (以每日 8 小時估計)
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  HR — 每日彙整  ──────────────────── */

function RecomputeModal({ defaultEmpId, defaultEmpLabel, onCancel, onDone }) {
  const toast = useToast();
  const [empId, setEmpId] = React.useState(defaultEmpId || null);
  const [empLabel, setEmpLabel] = React.useState(defaultEmpLabel || '');
  const [from, setFrom] = React.useState(() => {
    const d = new Date();
    return ymdStr(d.getFullYear(), d.getMonth() + 1, 1);
  });
  const [to, setTo] = React.useState(() => {
    const d = new Date();
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return ymdStr(d.getFullYear(), d.getMonth() + 1, lastDay);
  });
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const submit = async () => {
    if (!empId) { toast('請選擇員工', 'error'); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api('POST', '/attendance/daily/recompute', {
        employee_id: empId, from_date: from, to_date: to,
      });
      setResult(r);
      toast(`已重算 ${r.days_processed} 天`, 'ok');
    } catch (e) { toast(e.detail || '重算失敗', 'error'); } finally { setBusy(false); }
  };

  return (
    <Modal
      open onClose={busy ? null : onCancel}
      title="重新彙整出勤資料"
      subtitle="Recompute Daily"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>關閉</Button>
          {result && <Button variant="secondary" onClick={onDone}>完成並重新整理</Button>}
          <Button variant="primary" onClick={submit} disabled={busy || !empId}>{busy ? '計算中…' : '開始重算'}</Button>
        </>
      }
    >
      <p style={{ fontSize: FontSizes.xs, color: HR.inkSoft, lineHeight: 1.6, marginTop: 0 }}>
        從 <code style={{ background: HR.ruleSoft, padding: '1px 5px', borderRadius: uiPick(3, GxT.radius.chipSm) }}>punch_records</code> 重新聚合到 <code style={{ background: HR.ruleSoft, padding: '1px 5px', borderRadius: uiPick(3, GxT.radius.chipSm) }}>attendance_daily</code>。已存在的列僅更新 work_minutes，HR 手填的欄位（overtime/late）會保留。
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 8 }}>
        <Field label="員工" required span={2}>
          <EmployeePicker value={empId} valueLabel={empLabel} onChange={(id, label) => { setEmpId(id); setEmpLabel(label || ''); }} />
        </Field>
        <Field label="從" required>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="到" required>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle()} />
        </Field>
      </div>
      {result && (
        <div role={uiPick(undefined, 'status')} style={uiStyle({
          marginTop: 18, padding: 14, background: HR.accentSoft, borderRadius: 4,
          fontSize: FontSizes.xxs, fontFamily: HR.mono, color: HR.ink, lineHeight: 1.7,
        }, { padding: '14px 16px', background: GxT.color.pc, color: GxT.color.onPc, borderRadius: GxT.radius.inner, fontSize: 13, fontFamily: GxT.font, fontWeight: 600, fontVariantNumeric: 'tabular-nums' })}>
          <div>處理天數：{result.days_processed}</div>
          <div>新增 / 更新：{result.rows_inserted} / {result.rows_updated}</div>
          <div>總工時：{fmtDuration(result.total_work_minutes)}</div>
        </div>
      )}
    </Modal>
  );
}

// 統計卡 — 出勤率 / 總工時 / 加班 / 遲到
function DailyStatCards({ items }) {
  // 從 items 計算
  const stats = React.useMemo(() => {
    if (!items || items.length === 0) {
      return { attendance: 0, totalWorkMin: 0, overtimeMin: 0, lateCount: 0, totalDays: 0, presentDays: 0 };
    }
    const totalDays = items.length;
    const presentDays = items.filter(d =>
      d.actual_in && d.actual_out
    ).length;
    const attendance = totalDays > 0 ? Math.round((presentDays / totalDays) * 1000) / 10 : 0;
    const totalWorkMin = items.reduce((s, d) => s + (d.work_minutes || 0), 0);
    const overtimeMin = items.reduce((s, d) => s + (d.overtime_minutes || 0), 0);
    const lateCount = items.filter(d => (d.late_minutes || 0) > 0).length;
    return { attendance, totalWorkMin, overtimeMin, lateCount, totalDays, presentDays };
  }, [items]);

  const fmtHrs = (m) => `${Math.floor(m / 60)}h${m % 60 > 0 ? ` ${m % 60}m` : ''}`;

  const cards = [
    { label: '出勤率', value: `${stats.attendance}%`,
      sub: `${stats.presentDays}/${stats.totalDays} 天`, accent: HR.accent },
    { label: '總工時', value: stats.totalWorkMin > 0 ? fmtHrs(stats.totalWorkMin) : '—',
      sub: `平均 ${stats.totalDays > 0 ? Math.round(stats.totalWorkMin / stats.totalDays / 6) / 10 : 0} h/天`, accent: HR.ink },
    { label: '加班時數', value: stats.overtimeMin > 0 ? fmtHrs(stats.overtimeMin) : '—',
      sub: stats.overtimeMin > 0 ? '需關注工時管理' : '無加班',
      accent: stats.overtimeMin > 0 ? HR.warn : HR.inkFaint },
    { label: '遲到次數', value: `${stats.lateCount} 次`,
      sub: stats.totalDays > 0 ? `${Math.round(stats.lateCount / stats.totalDays * 100)}% 的天數` : '—',
      accent: stats.lateCount > 0 ? HR.down : HR.inkFaint },
  ];

  // Expressive: tonal KPI tiles (mockup .kpis — s1 surface, radius 12, value 800 tabular)
  const gc = GxT.color;
  return (
    <div className="hr-attendance-summary-grid" style={uiStyle({
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
      padding: '14px 24px', borderBottom: `1px solid ${HR.ruleSoft}`,
    }, { gap: 10, padding: '16px 20px 8px', borderBottom: 'none' })}>
      {cards.map((c, i) => (
        <div key={i} style={uiStyle({
          background: HR.surface, border: `1px solid ${HR.ruleSoft}`,
          borderRadius: 4, padding: '12px 14px',
          borderLeft: `3px solid ${c.accent}`,
        }, { background: gc.s1, border: 'none', borderLeft: 'none', borderRadius: GxT.radius.inner, padding: '12px 14px', minWidth: 0 })}>
          <div style={uiStyle({ fontSize: FontSizes.xxs, color: HR.inkFaint, letterSpacing: '0.1em', textTransform: 'uppercase' }, { fontSize: 12, fontWeight: 700, color: gc.onV, letterSpacing: 0, textTransform: 'none' })}>
            {c.label}
          </div>
          <div style={uiStyle({ marginTop: 6, fontSize: FontSizes.sm, fontWeight: 600, fontFamily: HR.serif, color: c.accent }, { marginTop: 4, fontSize: 22, fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' })}>
            {c.value}
          </div>
          <div style={uiStyle({ marginTop: 2, fontSize: FontSizes.xxs, color: HR.inkSoft }, { marginTop: 4 })}>
            {c.sub}
          </div>
        </div>
      ))}
    </div>
  );
}


function DailyTab({ employees, selectedEmpId, selectedEmpLabel, selectEmployee, departments, deptId, setDeptId, attendanceReloadKey, selfMode = false }) {
  const toast = useToast();
  const statusEnum = useEnum('attendance_status');
  const [yearMonth, setYearMonth] = React.useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });
  const dateRange = React.useMemo(() => {
    const { y, m } = yearMonth;
    const lastDay = new Date(y, m, 0).getDate();
    return { from: ymdStr(y, m, 1), to: ymdStr(y, m, lastDay) };
  }, [yearMonth]);
  const monthLabel = `${yearMonth.y} 年 ${yearMonth.m} 月`;
  const goPrevMonth = () => setYearMonth(({ y, m }) => m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 });
  const goNextMonth = () => setYearMonth(({ y, m }) => m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 });
  const goToday = () => {
    const d = new Date();
    setYearMonth({ y: d.getFullYear(), m: d.getMonth() + 1 });
  };
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(50);
  const [data, setData] = React.useState({ items: [], total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [recomputeOpen, setRecomputeOpen] = React.useState(false);
  // selfMode（一般員工）：鎖定只看自己，內部解析自己的 employee_id，不顯示員工選擇器
  const [selfEmpId, setSelfEmpId] = React.useState(null);
  const [selfResolved, setSelfResolved] = React.useState(false);

  React.useEffect(() => {
    if (!selfMode) return;
    api('GET', '/employees/me')
      .then(me => setSelfEmpId(me?.id || null))
      .catch(() => setSelfEmpId(null))
      .finally(() => setSelfResolved(true));
  }, [selfMode]);

  // selfMode 用自己的 emp_id；否則用跨 tab 共用的選擇
  const empId = selfMode ? selfEmpId : selectedEmpId;

  React.useEffect(() => { setPage(1); }, [empId, dateRange.from, dateRange.to]);

  const refresh = React.useCallback(() => {
    // selfMode 尚未解析完自己的 emp_id → 等待（避免空查詢）
    if (selfMode && !selfResolved) return;
    // 無有效員工 → 清空（非 selfMode 即「未選員工」）
    if (!empId) { setData({ items: [], total: 0 }); setLoading(false); return; }
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set('page', page); qs.set('page_size', pageSize);
    qs.set('employee_id', empId);
    if (dateRange.from) qs.set('from_date', dateRange.from);
    if (dateRange.to) qs.set('to_date', dateRange.to);
    // 把「有排班但無打卡」的缺勤日也補進清單（後端合成虛擬列）
    qs.set('include_absent', 'true');
    api('GET', `/attendance/daily?${qs.toString()}`)
      .then(setData).catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [page, pageSize, empId, selfMode, selfResolved, dateRange.from, dateRange.to, toast]);

  React.useEffect(() => { refresh(); }, [refresh]);
  // 訂閱外部 reload 訊號（例如 SchedulesTab 儲存排班後）
  React.useEffect(() => {
    if (attendanceReloadKey) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceReloadKey]);

  return (
    <>
      <Card className="hr-attendance-daily-card" padding="0" eyebrow="Daily Aggregate" title="每日出勤彙整" italic={`${data.total || 0} 筆`} right={
        selfMode ? null : (
          <Button variant="primary" size="md" icon="refresh" onClick={() => setRecomputeOpen(true)}>重算彙整</Button>
        )
      }>
        {!selfMode && (
          <DeptEmployeePicker
            departments={departments}
            deptId={deptId}
            setDeptId={setDeptId}
            employees={employees}
            selectedEmpId={selectedEmpId}
            selectEmployee={selectEmployee}
            includeAll={false}
          />
        )}
        {(selfMode && selfResolved && !selfEmpId) ? (
          <EmptyState icon="user" title="尚未綁定員工" hint="您的帳號尚未綁定員工，請聯絡 HR。" />
        ) : (!selfMode && !selectedEmpId) ? (
          <EmptyState icon="user" title="請先選擇員工" hint="從上方選擇部門與員工以查看每日出勤彙整。" />
        ) : (
        <>
        <DailyStatCards items={data.items} />
        <div style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '14px 24px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
        }, { padding: '8px 20px 12px', borderBottom: `1px solid ${GxT.color.s3}`, background: 'transparent' })}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={goPrevMonth} title="上個月" style={calNavBtnStyle()}>
              <span style={{ display: 'inline-block', transform: 'rotate(180deg)' }}>
                <Icon name="chevron-right" size={14} />
              </span>
            </button>
            <div style={uiStyle({
              minWidth: 120, textAlign: 'center', fontFamily: HR.mono,
              fontSize: FontSizes.xs, fontWeight: 500,
            }, { fontFamily: GxT.font, fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' })}>{monthLabel}</div>
            <button onClick={goNextMonth} title="下個月" style={calNavBtnStyle()}>
              <Icon name="chevron-right" size={14} />
            </button>
            <button onClick={goToday} title="跳到本月"
              style={{ ...calNavBtnStyle(), padding: '0 12px', width: 'auto', fontSize: uiPick(FontSizes.xxs, 13), color: HR.ink }}>
              今天
            </button>
          </div>
        </div>

        {loading ? (
          <SkeletonTable rows={6} />
        ) : data.items.length === 0 ? (
          <EmptyState icon="layout" title="無出勤紀錄" hint="點「重算彙整」從打卡紀錄計算工時。" />
        ) : (
          <div className="hr-attendance-daily-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={uiStyle({ background: HR.surfaceAlt, fontSize: FontSizes.xxs, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }, { letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontSize: 12 })}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>員工</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>日期</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>排班</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>實際</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>工時</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>缺勤工時</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>加班</th>
                <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>遲到</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(d => {
                const absent = !d.actual_in && !d.actual_out;
                const hasLate = (d.late_minutes || 0) > 0;
                const hasOT = (d.overtime_minutes || 0) > 0;

                let scheduledMinutes = 0;
                if (d.scheduled_in && d.scheduled_out) {
                  try {
                    const [sh, sm] = d.scheduled_in.split(':').map(Number);
                    const [eh, em] = d.scheduled_out.split(':').map(Number);
                    scheduledMinutes = Math.max(0, (eh * 60 + em) - (sh * 60 + sm) - 30);
                  } catch { /* skip */ }
                }
                const shortfallMinutes = Math.max(0, scheduledMinutes - (d.work_minutes || 0));
                const hasShortfall = shortfallMinutes > 0;

                const rowBg = absent
                  ? GxCore.alpha(HR.down, '10')
                  : (hasLate ? GxCore.alpha(HR.down, '06') : (hasOT ? GxCore.alpha(HR.warn, '06') : 'transparent'));
                return (
                  <tr key={d.id ?? `absent-${d.work_date}`} style={{
                    borderTop: `1px solid ${HR.ruleSoft}`,
                    background: rowBg,
                  }}>
                    <td style={{ padding: '13px 16px 13px 24px', fontWeight: 500 }}>{d.employee_name || `#${d.employee_id}`}</td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>{d.work_date}</td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                      {d.scheduled_in && d.scheduled_out ? `${d.scheduled_in}–${d.scheduled_out}` : '—'}
                    </td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: absent ? HR.down : HR.inkSoft }}>
                      {absent ? '⚠ 缺勤' : (d.actual_in && d.actual_out ? `${fmtTime(d.actual_in)}–${fmtTime(d.actual_out)}` : '—')}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 500 }}>{fmtDuration(d.work_minutes)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: hasShortfall ? HR.down : HR.inkFaint, fontWeight: hasShortfall ? 600 : 400 }}>{hasShortfall ? fmtDuration(shortfallMinutes) : '—'}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: hasOT ? HR.warn : HR.inkFaint, fontWeight: hasOT ? 600 : 400 }}>{hasOT ? fmtDuration(d.overtime_minutes) : '—'}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: hasLate ? HR.down : HR.inkFaint, fontWeight: hasLate ? 600 : 400 }}>{hasLate ? fmtDuration(d.late_minutes) : '—'}</td>
                    <td style={{ padding: '13px 24px 13px 16px' }}>
                      {d.status ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusEnum.color(d.status) || HR.inkFaint }} />
                          {statusEnum.label(d.status)}
                          {((d.leaves && d.leaves.length)
                              ? d.leaves.map(l => l.leave_type_name || l.leave_type)
                              : (d.leave_type_name ? [d.leave_type_name] : [])
                           ).filter(Boolean).map((nm, li) => (
                            <span key={li} style={{ color: uiPick('#8B5CF6', GxT.color.done), fontWeight: uiPick(500, 700), marginLeft: 4 }}>
                              ({nm})
                            </span>
                          ))}
                        </span>
                      ) : <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        <Pagination
          page={data.page || page}
          pageSize={data.page_size || pageSize}
          total={data.total || 0}
          onPageChange={setPage}
        />
        </>
        )}
      </Card>

      {recomputeOpen && (
        <RecomputeModal
          defaultEmpId={selectedEmpId}
          defaultEmpLabel={selectedEmpLabel}
          onCancel={() => setRecomputeOpen(false)}
          onDone={() => { setRecomputeOpen(false); refresh(); }}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Shared HR widgets  ───────────────── */

// 員工 chips bar — 三個 HR tab 共用，水平捲動，含「全部」chip
/* DeptEmployeePicker — 統一的「部門 → 員工」選擇器。
 * 取代舊的 EmployeeChipsBar（chips 橫條），與月曆檢視一致：
 *   ┌─ 部門 ▼ ──────┐  ┌─ 員工 ▼ ──────┐  [right: 自訂內容]
 *   └────────────────┘  └────────────────┘
 *
 * Props:
 *   departments      — 由 AttendancePage 統一撈
 *   deptId, setDeptId — 部門過濾狀態（提升至 AttendancePage 跨 tab 共用）
 *   employees        — 員工總清單（由 sharedProps 帶入）
 *   selectedEmpId, selectEmployee — 跨 tab 共用的員工選擇
 *   includeAll       — 員工下拉是否允許「全部」（SchedulesTab 必選 → false）
 *   right            — 右側可塞統計、按鈕等
 */
function DeptEmployeePicker({
  departments, deptId, setDeptId,
  employees, selectedEmpId, selectEmployee,
  includeAll = true, right = null,
}) {
  // 依部門過濾員工
  const filteredEmployees = React.useMemo(() => {
    if (!deptId) return employees || [];
    return (employees || []).filter(e => String(e.department_id) === String(deptId));
  }, [employees, deptId]);

  // 切部門：若目前員工不在新部門 → 清空
  React.useEffect(() => {
    if (selectedEmpId && filteredEmployees.length && !filteredEmployees.some(e => e.id === selectedEmpId)) {
      selectEmployee(null, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptId]);

  return (
    <div style={uiStyle({
      display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end',
      padding: '14px 24px', borderBottom: `1px solid ${HR.ruleSoft}`, background: HR.surface,
    }, { gap: 12, padding: '14px 20px', borderBottom: `1px solid ${GxT.color.s3}`, background: 'transparent' })}>
      <div style={{ minWidth: 180 }}>
        <CalendarLabel>部門</CalendarLabel>
        <Select
          value={deptId}
          onChange={v => setDeptId(v ? Number(v) : null)}
          placeholder="全部部門"
          options={(departments || []).map(d => ({ value: d.id, label: d.name }))}
        />
      </div>
      <div style={{ minWidth: 220 }}>
        <CalendarLabel>員工</CalendarLabel>
        <Select
          value={selectedEmpId}
          allowEmpty={true}
          onChange={v => {
            const id = v ? Number(v) : null;
            const emp = filteredEmployees.find(e => e.id === id);
            selectEmployee(id, emp?.name || '');
          }}
          placeholder={includeAll ? '全部員工' : '— 請選擇員工 —'}
          options={filteredEmployees.map(e => ({
            value: e.id,
            label: e.emp_code ? `${e.name} (${e.emp_code})` : e.name,
          }))}
        />
      </div>
      {right && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>{right}</div>}
    </div>
  );
}

// 保留 chipStyle 給其他可能引用點（雖然 EmployeeChipsBar 已移除）
function EmployeeChipsBar() { return null; }

function chipStyle(active) {
  return {
    padding: '5px 12px', borderRadius: uiPick(14, GxT.radius.chip), fontSize: FontSizes.xxs,
    border: `1px solid ${active ? HR.accent : HR.ruleSoft}`,
    background: active ? HR.accent : HR.surface,
    color: active ? '#fff' : HR.ink,
    cursor: 'pointer', flexShrink: 0,
    fontFamily: HR.sans,
  };
}

// 快捷日期按鈕 — 今日 / 昨日 / 本週 / 本月 / 近 7 日 / 近 30 日
function DateRangeQuickButtons({ value, onChange }) {
  const today = todayStr();
  const presets = [
    { label: '今日',   range: () => [today, today] },
    { label: '昨日',   range: () => [nDaysAgoStr(1), nDaysAgoStr(1)] },
    { label: '本週',   range: () => {
      const d = new Date();
      const day = d.getDay() === 0 ? 7 : d.getDay();  // ISO weekday
      return [nDaysAgoStr(day - 1), today];
    }},
    { label: '本月',   range: () => {
      const d = new Date();
      const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      return [first, today];
    }},
    { label: '近 7 日',  range: () => [nDaysAgoStr(6), today] },
    { label: '近 30 日', range: () => [nDaysAgoStr(29), today] },
  ];

  const matchedKey = presets.find(p => {
    const [f, t] = p.range();
    return f === value?.from && t === value?.to;
  })?.label;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
    }}>
      {presets.map(p => {
        const active = matchedKey === p.label;
        return (
          <button
            key={p.label}
            onClick={() => {
              const [from, to] = p.range();
              onChange({ from, to });
            }}
            style={{
              padding: '4px 10px', borderRadius: uiPick(3, GxT.radius.chip), fontSize: FontSizes.xxs,
              border: `1px solid ${active ? HR.accent : HR.ruleSoft}`,
              background: active ? HR.accent : HR.surface,
              color: active ? '#fff' : HR.inkSoft,
              cursor: 'pointer', whiteSpace: 'nowrap',
              fontFamily: HR.sans,
            }}
          >{p.label}</button>
        );
      })}
    </div>
  );
}


/* ─────────────────────────  HR — 排班規則  ───────────────────── */

// weekday 採 ISO 1-7（1=週一 ... 7=週日）。WEEKDAYS 既有定義為 1-7 index。
const SCHED_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

function defaultSchedule() {
  return SCHED_WEEKDAYS.map(wd => ({
    weekday: wd,
    active: wd >= 1 && wd <= 5,
    start_time: '09:00',
    end_time: '18:00',
    break_minutes: 60,
  }));
}

function loadScheduleFromRows(rows) {
  // rows: list of work_schedule entries from API
  const base = defaultSchedule().map(d => ({ ...d, active: false }));
  for (const r of rows || []) {
    const i = SCHED_WEEKDAYS.indexOf(r.weekday);
    if (i < 0) continue;
    base[i] = {
      weekday: r.weekday,
      active: !!(r.start_time && r.end_time),
      start_time: r.start_time || '09:00',
      end_time: r.end_time || '18:00',
      break_minutes: r.break_minutes ?? 60,
    };
  }
  return base;
}

function avgHours(rows) {
  const totals = [];
  for (const r of rows) {
    if (!r.active || !r.start_time || !r.end_time) continue;
    try {
      const [sh, sm] = r.start_time.split(':').map(Number);
      const [eh, em] = r.end_time.split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm) - (parseInt(r.break_minutes, 10) || 0);
      if (mins > 0) totals.push(mins / 60);
    } catch { /* skip */ }
  }
  if (!totals.length) return 0;
  return Math.round((totals.reduce((a, b) => a + b, 0) / totals.length) * 10) / 10;
}

function SchedulesTab({ employees, selectedEmpId, selectedEmpLabel, selectEmployee, departments, deptId, setDeptId, reloadAttendance }) {
  const toast = useToast();
  const empId = selectedEmpId;
  const empLabel = selectedEmpLabel;
  const [rows, setRows] = React.useState(defaultSchedule());
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [hasData, setHasData] = React.useState(false);

  // 載入該員工目前的 schedules
  React.useEffect(() => {
    if (!empId) {
      setRows(defaultSchedule());
      setHasData(false);
      return;
    }
    setLoading(true);
    api('GET', `/attendance/schedules?employee_id=${empId}`)
      .then(list => {
        const arr = Array.isArray(list) ? list.filter(r => r.employee_id === empId) : [];
        setRows(loadScheduleFromRows(arr));
        setHasData(arr.length > 0);
      })
      .catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [empId, toast]);

  const updateRow = (idx, patch) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const copyToWorkdays = (sourceIdx) => {
    const src = rows[sourceIdx];
    if (!src.active) { toast('來源日為休息，無法複製', 'error'); return; }
    setRows(prev => prev.map((r, i) =>
      (i !== sourceIdx && SCHED_WEEKDAYS[i] >= 1 && SCHED_WEEKDAYS[i] <= 5)
        ? { ...r, active: true, start_time: src.start_time, end_time: src.end_time, break_minutes: src.break_minutes }
        : r
    ));
    toast('已複製到週一~五', 'ok');
  };

  const submit = async () => {
    if (!empId) { toast('請先選擇員工', 'error'); return; }
    const active = rows.filter(r => r.active && r.start_time && r.end_time);
    if (!active.length) { toast('至少需設定 1 個工作日', 'error'); return; }

    for (const r of active) {
      const [sh, sm] = r.start_time.split(':').map(Number);
      const [eh, em] = r.end_time.split(':').map(Number);
      const work_min = (eh * 60 + em) - (sh * 60 + sm) - (parseInt(r.break_minutes, 10) || 0);
      if (work_min <= 0) {
        toast(`週${WEEKDAYS[r.weekday]} 扣除午休後工時須 > 0`, 'error');
        return;
      }
    }

    const payload = active.map(r => ({
      weekday: r.weekday,
      start_time: r.start_time,
      end_time: r.end_time,
      break_minutes: parseInt(r.break_minutes, 10) || 0,
    }));

    setSaving(true);
    try {
      await api('PATCH', `/employees/${empId}`, { work_schedule: payload });
      toast(`${empLabel || '員工'} 的排班已更新（${active.length} 個工作日）`, 'ok');
      setHasData(true);
      // 通知月曆 / 每日彙整 tab 重新拉資料（後端已自動 recompute）
      if (reloadAttendance) reloadAttendance();
    } catch (e) { toast('儲存失敗：' + (e.detail || ''), 'error'); }
    finally { setSaving(false); }
  };

  const avg = avgHours(rows);

  return (
    <Card className="hr-attendance-schedules-card" padding="0" eyebrow="Schedules" title="排班規則" italic="員工每週工時" right={
      empId && (
        <Button variant="primary" size="md" onClick={submit} disabled={saving || loading}>
          {saving ? '儲存中…' : (hasData ? '儲存變更' : '建立排班')}
        </Button>
      )
    }>
      <DeptEmployeePicker
        departments={departments}
        deptId={deptId}
        setDeptId={setDeptId}
        employees={employees}
        selectedEmpId={empId}
        selectEmployee={selectEmployee}
        includeAll={false}
      />
      <div style={uiStyle({
        padding: '14px 24px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
      }, { padding: '12px 20px', borderBottom: `1px solid ${GxT.color.s3}`, background: 'transparent' })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, maxWidth: 400 }}>
            <EmployeePicker
              value={empId}
              valueLabel={empLabel}
              onChange={(id, label) => selectEmployee(id, label || '')}
              placeholder="輸入姓名搜尋…"
            />
          </div>
          {empId && (
            <div style={{ fontSize: FontSizes.xxs, color: HR.inkSoft }}>
              {hasData ? '已有排班' : '尚未設定（將建立預設）'}
            </div>
          )}
        </div>
      </div>

      {!empId ? (
        <EmptyState
          icon="user"
          title="請先選擇員工"
          hint="從上方輸入姓名搜尋要設定排班的員工。"
        />
      ) : loading ? (
        <SkeletonTable rows={7} />
      ) : (
        <>
          <div className="hr-schedule-outer" style={{ padding: '14px 24px' }}>
            <div className="hr-schedule-row" style={uiStyle({
              display: 'grid',
              gridTemplateColumns: '60px 100px 1fr 1fr 1fr 1fr 110px',
              gap: 10, alignItems: 'center',
              fontSize: FontSizes.xxs, color: HR.inkFaint, letterSpacing: '0.1em',
              textTransform: 'uppercase', paddingBottom: 10,
              borderBottom: `1px solid ${HR.ruleSoft}`,
            }, { fontSize: 12, fontWeight: 700, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none' })}>
              <div>星期</div>
              <div>上班</div>
              <div>上班時間</div>
              <div>下班時間</div>
              <div>午休（分）</div>
              <div>工時</div>
              <div></div>
            </div>
            {rows.map((row, idx) => {
              let dailyHrs = 0;
              if (row.active && row.start_time && row.end_time) {
                try {
                  const [sh, sm] = row.start_time.split(':').map(Number);
                  const [eh, em] = row.end_time.split(':').map(Number);
                  const m = (eh * 60 + em) - (sh * 60 + sm) - (parseInt(row.break_minutes, 10) || 0);
                  if (m > 0) dailyHrs = Math.round(m / 60 * 100) / 100;
                } catch { /* skip */ }
              }
              return (
                <div key={row.weekday} className="hr-schedule-row" style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 100px 1fr 1fr 1fr 1fr 110px',
                  gap: 10, alignItems: 'center', padding: '10px 0',
                  borderBottom: `1px solid ${HR.ruleSoft}`,
                  opacity: row.active ? 1 : 0.5,
                }}>
                  <div style={{ fontFamily: HR.serif, fontSize: FontSizes.xs, fontWeight: uiPick(undefined, 700) }}>週{WEEKDAYS[row.weekday]}</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: FontSizes.xxs }}>
                    <input
                      type="checkbox"
                      checked={row.active}
                      onChange={(e) => updateRow(idx, { active: e.target.checked })}
                    />
                    {row.active ? '上班' : '休息'}
                  </label>
                  <input type="time" value={row.start_time} disabled={!row.active}
                         onChange={(e) => updateRow(idx, { start_time: e.target.value })}
                         style={inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs })} />
                  <input type="time" value={row.end_time} disabled={!row.active}
                         onChange={(e) => updateRow(idx, { end_time: e.target.value })}
                         style={inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs })} />
                  <input type="number" value={row.break_minutes} disabled={!row.active}
                         onChange={(e) => updateRow(idx, { break_minutes: e.target.value })}
                         style={inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xs, textAlign: 'right' })} />
                  <div style={{ fontFamily: HR.mono, textAlign: 'right', fontSize: FontSizes.xs, color: row.active ? HR.ink : HR.inkFaint }}>
                    {row.active ? `${dailyHrs} h` : '—'}
                  </div>
                  <div>
                    {row.active && (
                      <Button size="sm" variant="ghost"
                              onClick={() => copyToWorkdays(idx)}
                              title="複製此日設定到週一~五">
                        套用到平日
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="hr-attendance-schedule-footer" style={uiStyle({
              marginTop: 14, padding: '10px 12px', background: HR.surfaceAlt,
              borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }, { padding: '12px 16px', background: GxT.color.pc, color: GxT.color.onPc, borderRadius: GxT.radius.inner })}>
              <div style={uiStyle({ fontSize: FontSizes.xxs, color: HR.inkSoft }, { fontSize: 13, fontWeight: 600, color: GxT.color.onPc })}>
                平均每日工時（薪資計算用）
              </div>
              <div style={uiStyle({ fontFamily: HR.mono, fontWeight: 700, fontSize: FontSizes.xs }, { fontFamily: GxT.font, fontWeight: 800, fontSize: 17, fontVariantNumeric: 'tabular-nums' })}>
                {avg} 小時
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

/* ─────────────────────────  Page (entry)  ──────────────────── */

function AttendancePage() {
  const [user, setUser] = React.useState(null);
  const [tab, setTab] = React.useState('kiosk');
  // 跨 tab 共用：選中的員工 + 部門過濾 + 員工/部門清單
  const [selectedEmpId, setSelectedEmpId] = React.useState(null);
  const [selectedEmpLabel, setSelectedEmpLabel] = React.useState('');
  const [employees, setEmployees] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [deptId, setDeptId] = React.useState(null);

  React.useEffect(() => {
    api('GET', '/auth/me').then(setUser).catch(() => setUser({ roles: [] }));
  }, []);

  const isHR = isAttendanceManager(user);

  // HR 才需要員工 / 部門列表
  React.useEffect(() => {
    if (!isHR) return;
    // 用出勤系統自己的 roster（HR 對 /api/attendance 有權限；/employees 仍僅 admin）。
    // roster 回傳的是「陣列」而非分頁物件，含挑選器所需的 id/name/department_id/emp_code 等欄位。
    api('GET', '/attendance/roster')
      .then(r => setEmployees(Array.isArray(r) ? r : (r.items || [])))
      .catch(() => setEmployees([]));
    api('GET', '/departments')
      .then(r => setDepartments(Array.isArray(r) ? r : (r.items || [])))
      .catch(() => setDepartments([]));
  }, [isHR]);

  const selectEmployee = React.useCallback((id, label) => {
    setSelectedEmpId(id);
    setSelectedEmpLabel(label || '');
  }, []);

  // 全局 reload 觸發器 — 排班儲存後通知月曆 / 每日彙整重新拉資料
  const [attendanceReloadKey, setAttendanceReloadKey] = React.useState(0);
  const reloadAttendance = React.useCallback(() => setAttendanceReloadKey(k => k + 1), []);

  const tabs = [
    { key: 'kiosk', label: '打卡鐘', icon: 'clock' },
    { key: 'me', label: '我的紀錄', icon: 'file-text' },
    // 一般員工：月曆檢視 + 每日彙整（鎖自己、唯讀）；HR：月曆檢視 + 每日彙整（可選員工）
    ...(isHR ? [
      { key: 'calendar', label: '月曆檢視', icon: 'calendar' },
      { key: 'daily', label: '每日彙整', icon: 'layout' },
      // { key: 'schedules', label: '排班規則', icon: 'calendar' },  // 暫時隱藏
    ] : [
      { key: 'mycalendar', label: '月曆檢視', icon: 'calendar' },
      { key: 'mydaily', label: '每日彙整', icon: 'layout' },
    ]),
  ];

  const sharedProps = {
    employees, selectedEmpId, selectedEmpLabel, selectEmployee,
    departments, deptId, setDeptId,
    attendanceReloadKey, reloadAttendance,
  };

  return (
    <>
      <PageHeader
        eyebrow="出勤管理 · Attendance"
        title="出勤"
        italic={isHR ? 'Attendance Console' : 'My Attendance'}
      />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'kiosk' && <KioskTab user={user} />}
      {tab === 'me' && <MyAttendanceTab user={user} />}
      {tab === 'calendar' && <CalendarTab {...sharedProps} />}
      {tab === 'mycalendar' && <CalendarTab {...sharedProps} selfMode />}
      {tab === 'daily' && <DailyTab {...sharedProps} />}
      {tab === 'mydaily' && <DailyTab {...sharedProps} selfMode />}
      {tab === 'schedules' && <SchedulesTab {...sharedProps} />}
    </>
  );
}

window.AttendancePage = AttendancePage;
